import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaHome, 
  FaCalendarAlt, 
  FaClock,
  FaEnvelope, 
  FaCog, 
  FaPlus,
  FaEye,
  FaEdit,
  FaTrash,
  FaMapMarkerAlt,
  FaStar,
  FaUsers,
  FaRupeeSign,
  FaUser,
  FaSignOutAlt,
  FaToggleOn,
  FaToggleOff,
  FaBell,
  FaComments,
  FaBars,
  FaTimes,
  FaCheckCircle,
  FaTimesCircle,
  FaQuestionCircle,
  FaSpinner
} from 'react-icons/fa';
import Navbar from '../components/Navbar';
import LoginModal from '../components/LoginModal';
import HostOnboarding from '../components/HostOnboarding';
import AddPropertyModal from '../components/AddPropertyModal';
import EditPropertyModal from '../components/EditPropertyModal';
import PropertyTypeModal from '../components/PropertyTypeModal';
import AddMeetingRoomForm from '../components/AddMeetingRoomForm';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import BookingActionConfirmModal from '../components/BookingActionConfirmModal';
import MeetingBookingNotificationCard from '../components/MeetingBookingNotificationCard';
import HostMessages from '../components/HostMessages';
import { fetchHostMessages } from '../services/messagingService';
import { roomsAPI } from '../services/meetingsAPI';
import { setDefaultHostId } from '../utils/hostHelper';
import { getPropertyImage, getProfileImageUrl } from '../utils/imageHelper';
import { getApiUrl } from '../config/api';
import useSafeToast from '../utils/safeToast';
import './HostPage.css';

const HOST_DASHBOARD_ROLES = new Set(['host', 'company_admin', 'company_manager', 'company_staff']);
const COMPANY_ROLES = new Set(['company_admin', 'company_manager', 'company_staff']);
const COMPANY_WRITE_ROLES = new Set(['company_admin', 'company_manager', 'company_staff']);

const getCompanyThemeColor = (company) => company?.branding?.themeColor || '#0B6E4F';

const canAccessHostDashboard = (user) => {
  if (!user) return false;
  const role = String(user.role || '');
  const hasCompany = Boolean(user?.company?._id || user?.company);
  return HOST_DASHBOARD_ROLES.has(role) || hasCompany;
};

const canManageListingsForUser = (user) => {
  if (!user) return false;
  const role = String(user.role || '');
  const hasCompany = Boolean(user?.company?._id || user?.company);
  if (hasCompany && COMPANY_ROLES.has(role)) {
    return COMPANY_WRITE_ROLES.has(role) || role === 'admin';
  }
  return role === 'host' || role === 'admin';
};

// ── Host Settings Panel ──────────────────────────────────────────
const HostSettingsPanel = () => {
  const [activeGroup, setActiveGroup] = useState('profile');
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      return {
        name: u.name || '',
        email: u.email || '',
        phone: u.phone || u.phoneNumber || '',
        language: 'English',
        currency: 'INR',
        emailNotif: true,
        smsNotif: false,
        pushNotif: true,
        bookingAlerts: true,
        reviewAlerts: true,
        payoutMethod: 'bank',
        accountNumber: '',
        ifsc: '',
        upi: '',
        twoFactor: false,
        autoAccept: false,
        instantBook: true,
      };
    } catch { return {}; }
  });

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const groups = [
    { id: 'profile',       label: 'Profile',        icon: FaUser },
    { id: 'notifications', label: 'Notifications',  icon: FaBell },
    { id: 'payout',        label: 'Payout',         icon: FaRupeeSign },
    { id: 'security',      label: 'Security',       icon: FaCog },
    { id: 'preferences',   label: 'Preferences',    icon: FaToggleOn },
  ];

  return (
    <div className="hsp-wrap">
      {/* Sidebar tabs */}
      <nav className="hsp-nav">
        {groups.map(g => {
          const Icon = g.icon;
          return (
            <button
              key={g.id}
              type="button"
              className={`hsp-nav-btn${activeGroup === g.id ? ' hsp-nav-btn--active' : ''}`}
              onClick={() => setActiveGroup(g.id)}
            >
              <Icon className="hsp-nav-icon" />
              {g.label}
            </button>
          );
        })}
      </nav>

      {/* Content */}
      <div className="hsp-content">
        {activeGroup === 'profile' && (
          <div className="hsp-card">
            <h2 className="hsp-card-title">Profile Information</h2>
            <div className="hsp-field-grid">
              <div className="hsp-field">
                <label>Full Name</label>
                <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Your name" />
              </div>
              <div className="hsp-field">
                <label>Email Address</label>
                <input value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@example.com" type="email" />
              </div>
              <div className="hsp-field">
                <label>Phone Number</label>
                <input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+91 XXXXX XXXXX" />
              </div>
              <div className="hsp-field">
                <label>Display Language</label>
                <select value={form.language} onChange={e => set('language', e.target.value)}>
                  {['English','Hindi','Kannada','Tamil','Telugu'].map(l => <option key={l}>{l}</option>)}
                </select>
              </div>
            </div>
          </div>
        )}

        {activeGroup === 'notifications' && (
          <div className="hsp-card">
            <h2 className="hsp-card-title">Notification Preferences</h2>
            <div className="hsp-toggle-list">
              {[
                { key: 'emailNotif',   label: 'Email Notifications',   desc: 'Receive updates via email' },
                { key: 'smsNotif',     label: 'SMS Notifications',     desc: 'Get SMS alerts for bookings' },
                { key: 'pushNotif',    label: 'Push Notifications',    desc: 'Browser push alerts' },
                { key: 'bookingAlerts',label: 'New Booking Alerts',    desc: 'Alert when a guest books' },
                { key: 'reviewAlerts', label: 'Review Alerts',         desc: 'Alert when a guest reviews' },
              ].map(item => (
                <div key={item.key} className="hsp-toggle-row">
                  <div>
                    <div className="hsp-toggle-label">{item.label}</div>
                    <div className="hsp-toggle-desc">{item.desc}</div>
                  </div>
                  <button
                    type="button"
                    className={`hsp-toggle${form[item.key] ? ' hsp-toggle--on' : ''}`}
                    onClick={() => set(item.key, !form[item.key])}
                    aria-pressed={form[item.key]}
                  >
                    <span className="hsp-toggle-thumb" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeGroup === 'payout' && (
          <div className="hsp-card">
            <h2 className="hsp-card-title">Payout Settings</h2>
            <div className="hsp-radio-group">
              {[{v:'bank',l:'Bank Transfer'},{v:'upi',l:'UPI'}].map(o => (
                <label key={o.v} className={`hsp-radio-card${form.payoutMethod===o.v?' hsp-radio-card--active':''}`}>
                  <input type="radio" name="payout" value={o.v} checked={form.payoutMethod===o.v} onChange={() => set('payoutMethod', o.v)} />
                  {o.l}
                </label>
              ))}
            </div>
            {form.payoutMethod === 'bank' ? (
              <div className="hsp-field-grid">
                <div className="hsp-field">
                  <label>Account Number</label>
                  <input value={form.accountNumber} onChange={e => set('accountNumber', e.target.value)} placeholder="XXXX XXXX XXXX" />
                </div>
                <div className="hsp-field">
                  <label>IFSC Code</label>
                  <input value={form.ifsc} onChange={e => set('ifsc', e.target.value)} placeholder="SBIN0001234" />
                </div>
              </div>
            ) : (
              <div className="hsp-field" style={{marginTop:'1rem'}}>
                <label>UPI ID</label>
                <input value={form.upi} onChange={e => set('upi', e.target.value)} placeholder="yourname@upi" />
              </div>
            )}
          </div>
        )}

        {activeGroup === 'security' && (
          <div className="hsp-card">
            <h2 className="hsp-card-title">Security</h2>
            <div className="hsp-toggle-list">
              <div className="hsp-toggle-row">
                <div>
                  <div className="hsp-toggle-label">Two-Factor Authentication</div>
                  <div className="hsp-toggle-desc">Add an extra layer of security to your account</div>
                </div>
                <button
                  type="button"
                  className={`hsp-toggle${form.twoFactor ? ' hsp-toggle--on' : ''}`}
                  onClick={() => set('twoFactor', !form.twoFactor)}
                  aria-pressed={form.twoFactor}
                >
                  <span className="hsp-toggle-thumb" />
                </button>
              </div>
            </div>
            <div className="hsp-field" style={{marginTop:'1.25rem'}}>
              <label>Current Password</label>
              <input type="password" placeholder="••••••••" />
            </div>
            <div className="hsp-field-grid" style={{marginTop:'0.75rem'}}>
              <div className="hsp-field">
                <label>New Password</label>
                <input type="password" placeholder="••••••••" />
              </div>
              <div className="hsp-field">
                <label>Confirm Password</label>
                <input type="password" placeholder="••••••••" />
              </div>
            </div>
          </div>
        )}

        {activeGroup === 'preferences' && (
          <div className="hsp-card">
            <h2 className="hsp-card-title">Hosting Preferences</h2>
            <div className="hsp-toggle-list">
              {[
                { key: 'autoAccept',  label: 'Auto-Accept Bookings',  desc: 'Automatically confirm new booking requests' },
                { key: 'instantBook', label: 'Instant Book',          desc: 'Allow guests to book without approval' },
              ].map(item => (
                <div key={item.key} className="hsp-toggle-row">
                  <div>
                    <div className="hsp-toggle-label">{item.label}</div>
                    <div className="hsp-toggle-desc">{item.desc}</div>
                  </div>
                  <button
                    type="button"
                    className={`hsp-toggle${form[item.key] ? ' hsp-toggle--on' : ''}`}
                    onClick={() => set(item.key, !form[item.key])}
                    aria-pressed={form[item.key]}
                  >
                    <span className="hsp-toggle-thumb" />
                  </button>
                </div>
              ))}
            </div>
            <div className="hsp-field" style={{marginTop:'1.25rem'}}>
              <label>Default Currency</label>
              <select value={form.currency} onChange={e => set('currency', e.target.value)}>
                {['INR','USD','EUR','GBP'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
        )}

        {/* Save button */}
        <div className="hsp-actions">
          {saved && <span className="hsp-saved">✓ Changes saved</span>}
          <button type="button" className="hsp-save-btn" onClick={handleSave}>
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};
// ────────────────────────────────────────────────────────────────

const HostPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useSafeToast();
  const [currentUser, setCurrentUser] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isScrolled, setIsScrolled] = useState(false);
  const [enquiries, setEnquiries] = useState([]);
  const [selectedEnquiryId, setSelectedEnquiryId] = useState(null);
  const [properties, setProperties] = useState([]);
  const [meetingRooms, setMeetingRooms] = useState([]);
  const [listingView, setListingView] = useState('stay'); // 'stay' | 'meeting'
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isHostProfileComplete, setIsHostProfileComplete] = useState(false);
  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);
  const [showPropertyTypeModal, setShowPropertyTypeModal] = useState(false);
  const [showListingTypePicker, setShowListingTypePicker] = useState(false);
  const [showMeetingRoomForm, setShowMeetingRoomForm] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationCategory, setNotificationCategory] = useState('hotel'); // 'meeting' | 'hotel'
  const [messages, setMessages] = useState([]);
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState(null);
  const [showMeetingRoomDeleteModal, setShowMeetingRoomDeleteModal] = useState(false);
  const [meetingRoomToDelete, setMeetingRoomToDelete] = useState(null);
  const [editingProperty, setEditingProperty] = useState(null);
  const [showEditPropertyModal, setShowEditPropertyModal] = useState(false);
  const [enquiryFilter, setEnquiryFilter] = useState('all'); // 'all', 'pending', 'approved', 'declined'
  const [enquiryTypeFilter, setEnquiryTypeFilter] = useState('all'); // 'all' | 'meeting' | 'hotel'
  const [enquirySearchTerm, setEnquirySearchTerm] = useState('');
  const [enquirySortBy, setEnquirySortBy] = useState('date-desc'); // 'date-desc', 'date-asc', 'amount-desc', 'amount-asc'
  const [selectedEnquiries, setSelectedEnquiries] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [enquiryActionLoading, setEnquiryActionLoading] = useState({}); // { enquiryId: 'approved' | 'declined' } to prevent double-click
  const [bookingActionConfirm, setBookingActionConfirm] = useState(null);
  const [bookingConfirmLoading, setBookingConfirmLoading] = useState(false);
  const isMountedRef = useRef(true);
  const [hostEarnings, setHostEarnings] = useState({ payments: [], summary: { totalEarned: 0, totalCommission: 0, netBalance: 0 } });
  const [hostPayouts, setHostPayouts] = useState([]);
  const [earningsLoading, setEarningsLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    dateOfBirth: '',
    emergencyContact: '',
    governmentId: '',
    governmentIdNumber: '',
    bio: '',
    languages: 'English, Hindi, Marathi',
    interests: ['Travel', 'Photography', 'Cooking', 'Architecture']
  });

  // Build profileData from user + hostProfile (onboarding details)
  const getProfileDataFromUser = (user, fallback) => {
    if (!user) return fallback || {};
    const hp = user.hostProfile || {};
    const locationParts = [hp.city, hp.state].filter(Boolean);
    return {
      name: user.name || '',
      email: user.email || '',
      phone: user.phoneNumber || user.phone || '',
      location: locationParts.length ? locationParts.join(', ') : (user.location || ''),
      address: hp.address || '',
      city: hp.city || '',
      state: hp.state || '',
      pincode: hp.pincode || '',
      dateOfBirth: hp.dateOfBirth || '',
      emergencyContact: hp.emergencyContact || '',
      governmentId: hp.governmentId || '',
      governmentIdNumber: hp.governmentIdNumber || '',
      bio: user.bio || (fallback && fallback.bio) || '',
      languages: user.languages || (fallback && fallback.languages) || 'English, Hindi, Marathi',
      interests: user.interests || (fallback && fallback.interests) || ['Travel', 'Photography', 'Cooking', 'Architecture']
    };
  };

  // Mock data for demonstration
  const mockEnquiries = [
    {
      id: 1,
      guestName: 'Rahul Sharma',
      guestEmail: 'rahul.sharma@email.com',
      propertyName: 'Luxury Villa in Goa',
      checkIn: '2024-01-15',
      checkOut: '2024-01-20',
      guests: 4,
      message: 'Hi, I would like to book your villa for a family vacation. Do you have availability for these dates?',
      status: 'pending',
      createdAt: '2024-01-10T10:30:00Z',
      totalAmount: 25000
    },
    {
      id: 2,
      guestName: 'Priya Patel',
      guestEmail: 'priya.patel@email.com',
      propertyName: 'Cozy Apartment in Mumbai',
      checkIn: '2024-01-25',
      checkOut: '2024-01-28',
      guests: 2,
      message: 'Looking for a comfortable stay for a business trip. Is parking available?',
      status: 'approved',
      createdAt: '2024-01-08T14:20:00Z',
      totalAmount: 12000
    },
    {
      id: 3,
      guestName: 'Amit Kumar',
      guestEmail: 'amit.kumar@email.com',
      propertyName: 'Beach House in Kerala',
      checkIn: '2024-02-10',
      checkOut: '2024-02-15',
      guests: 6,
      message: 'Planning a group trip. Can you accommodate 6 people comfortably?',
      status: 'declined',
      createdAt: '2024-01-05T09:15:00Z',
      totalAmount: 35000
    }
  ];

  const mockProperties = [
    {
      id: 1,
      name: 'Luxury Villa in Goa',
      location: 'North Goa, Goa',
      type: 'Villa',
      guests: 8,
      bedrooms: 4,
      bathrooms: 3,
      pricePerNight: 5000,
      rating: 4.8,
      reviews: 24,
      status: 'active',
      image: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=400'
    },
    {
      id: 2,
      name: 'Cozy Apartment in Mumbai',
      location: 'Bandra, Mumbai',
      type: 'Apartment',
      guests: 4,
      bedrooms: 2,
      bathrooms: 2,
      pricePerNight: 3000,
      rating: 4.6,
      reviews: 18,
      status: 'active',
      image: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=400'
    },
    {
      id: 3,
      name: 'Beach House in Kerala',
      location: 'Varkala, Kerala',
      type: 'House',
      guests: 10,
      bedrooms: 5,
      bathrooms: 4,
      pricePerNight: 7000,
      rating: 4.9,
      reviews: 31,
      status: 'inactive',
      image: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=400'
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      if (isMountedRef.current) setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    // Check if user is logged in
    const loadUser = () => {
      try {
        const storedUser = localStorage.getItem('user');
        const storedToken = localStorage.getItem('token');
        if (storedUser && storedToken) {
          const parsedUser = JSON.parse(storedUser);
          if (!isMountedRef.current) return;
          setCurrentUser(parsedUser);
          setProfileData(prev => getProfileDataFromUser(parsedUser, prev));

          let hostId = parsedUser._id || parsedUser.id;
          if (hostId) {
            if (typeof hostId === 'object') hostId = hostId.toString();
            setDefaultHostId(hostId);
            console.log('✅ Set default host ID to (string):', hostId);
            console.log('✅ Type:', typeof hostId);
          }

          // Migrate Google profile picture to local (avoids 429 from lh3.googleusercontent.com)
          const pic = parsedUser?.profilePicture;
          if (pic && typeof pic === 'string' && pic.includes('googleusercontent.com')) {
            const uid = parsedUser._id || parsedUser.id;
            const key = `migrateAttempted_${uid}`;
            if (!sessionStorage.getItem(key)) {
              sessionStorage.setItem(key, '1');
              fetch(getApiUrl('/api/auth/migrate-google-profile-picture'), {
                method: 'PATCH',
                headers: { Authorization: `Bearer ${storedToken}` }
              })
                .then((r) => r.ok && r.json())
                .then((data) => {
                  if (data?.success && data?.user?.profilePicture && isMountedRef.current) {
                    const updated = { ...parsedUser, profilePicture: data.user.profilePicture };
                    localStorage.setItem('user', JSON.stringify(updated));
                    setCurrentUser(updated);
                    window.dispatchEvent(new CustomEvent('userProfileUpdated'));
                  }
                })
                .catch(() => {});
            }
          }

          const hasHostAccess = canAccessHostDashboard(parsedUser);
          const hasCompanyContext = Boolean(parsedUser?.company?._id || parsedUser?.company);
          const requiresOnboarding =
            parsedUser.role === 'host' && !parsedUser.hostProfileComplete && !hasCompanyContext;

          if (hasHostAccess && (parsedUser.hostProfileComplete || hasCompanyContext)) {
            setIsHostProfileComplete(true);
            fetchProperties();
            fetchMeetingRooms(parsedUser);
            fetchEnquiries();
            loadNotificationsAndMessages(parsedUser);
          } else if (requiresOnboarding) {
            setShowOnboarding(true);
          } else {
            setShowOnboarding(true);
          }
        }
      } catch (error) {
        console.error('Error loading user:', error);
      } finally {
        if (isMountedRef.current) setLoading(false);
      }
    };

    loadUser();

    const handleProfileUpdate = () => loadUser();
    window.addEventListener('userProfileUpdated', handleProfileUpdate);

    const pollingInterval = setInterval(() => {
      if (!isMountedRef.current) return;
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        loadNotificationsAndMessages(user);
      }
    }, 20000);

    return () => {
      isMountedRef.current = false;
      clearInterval(pollingInterval);
      window.removeEventListener('userProfileUpdated', handleProfileUpdate);
    };
  }, []);

  // Open a specific tab when navigating from /host/messages (e.g. "View enquiries")
  useEffect(() => {
    const openTab = location.state?.openTab;
    if (openTab && ['dashboard', 'enquiries', 'earnings', 'notifications', 'messages', 'properties', 'calendar', 'profile', 'settings'].includes(openTab)) {
      setActiveTab(openTab);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.openTab, location.pathname, navigate]);

  const openEnquiryFromBooking = (bookingId, bookingType = 'hotel') => {
    const normalizedId = bookingId ? String(bookingId).trim() : '';
    setActiveTab('enquiries');
    setEnquiryFilter('all');
    setEnquiryTypeFilter(bookingType === 'meeting' ? 'meeting' : 'hotel');
    if (normalizedId) {
      setSelectedEnquiryId(normalizedId);
      setEnquirySearchTerm(normalizedId);
    } else {
      setSelectedEnquiryId(null);
      setEnquirySearchTerm('');
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    const bookingId = searchParams.get('bookingId');
    const bookingType = searchParams.get('type');

    if (tab !== 'enquiries') return;

    openEnquiryFromBooking(bookingId, bookingType === 'meeting' ? 'meeting' : 'hotel');
    navigate('/host', { replace: true });
  }, [location.search, navigate]);

  // Reset enquiry focus when leaving the enquiries tab
  useEffect(() => {
    if (activeTab !== 'enquiries') {
      setSelectedEnquiryId(null);
      setEnquiryTypeFilter('all');
      setEnquirySearchTerm('');
    }
  }, [activeTab]);

  useEffect(() => {
    if (!selectedEnquiryId || activeTab !== 'enquiries' || enquiries.length === 0) return undefined;

    const hasMatch = enquiries.some((enquiry) => String(enquiry.id) === String(selectedEnquiryId));
    if (!hasMatch) return undefined;

    const timer = window.setTimeout(() => {
      document
        .getElementById(`enquiry-card-${selectedEnquiryId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 180);

    return () => window.clearTimeout(timer);
  }, [selectedEnquiryId, activeTab, enquiries, enquiryTypeFilter, enquirySearchTerm]);

  // Host-only: notifications and messages for host dashboard; guest pages are unaffected (they use userType=guest).
  const loadNotificationsAndMessages = async (user) => {
    try {
      let userId = user._id || user.id || 'demo_host';
      
      // Convert ObjectId to string if needed
      if (userId && typeof userId === 'object') {
        userId = userId.toString();
      }
      
      console.log('Loading notifications for host:', userId);
      
      // Load notifications from MongoDB database (userType=host – not shown on guest pages)
      try {
        const token = localStorage.getItem('token');
        const url = `${getApiUrl('/api/notifications/my-notifications')}?userType=host`;
        console.log('Fetching notifications from database...');
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.status === 401) {
          if (isMountedRef.current) {
            setNotifications([]);
            setUnreadNotifCount(0);
          }
        } else {
          const result = await response.json();
          if (!isMountedRef.current) return;
          if (result.success) {
            // Hide host self-action notifications (approve/decline confirmations).
            // Owner should not receive these; guest alone should be notified.
            const filteredNotifications = (result.notifications || []).filter((notif) => {
              if (notif.userType !== 'host') return true;
              return !['booking_confirmed', 'booking_declined', 'booking_approved'].includes(notif.type);
            });

            const filteredUnreadCount = filteredNotifications.filter(n => !n.read).length;
            setNotifications(filteredNotifications);
            setUnreadNotifCount(filteredUnreadCount);
            console.log('Loaded', filteredNotifications.length, 'filtered notifications from MongoDB,', filteredUnreadCount, 'unread');
          } else {
            setNotifications([]);
            setUnreadNotifCount(0);
          }
        }
      } catch (dbError) {
        console.error('Database error:', dbError);
        if (isMountedRef.current) {
          setNotifications([]);
          setUnreadNotifCount(0);
        }
      }

      if (!isMountedRef.current) return;
      const hostMessages = await fetchHostMessages();
      setMessages(hostMessages);
      const unreadMsgCount = hostMessages.filter(m => !m.read).length;
      setUnreadMessageCount(unreadMsgCount);
      console.log('Loaded', hostMessages.length, 'messages,', unreadMsgCount, 'unread');
    } catch (error) {
      console.error('Error loading notifications and messages:', error);
    }
  };

  const fetchProperties = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/api/properties/my-properties'), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const result = await response.json();
      if (!isMountedRef.current) return;

      if (result.success) {
        const userId = currentUser?._id || currentUser?.id;
        const propertiesWithHost = result.properties.map(prop => ({
          ...prop,
          hostId: prop.hostId || userId,
          host: prop.host || {
            _id: userId,
            id: userId,
            name: currentUser?.name,
            email: currentUser?.email
          }
        }));
        setProperties(propertiesWithHost);
        console.log('✅ Loaded', propertiesWithHost.length, 'properties for host:', userId);
      } else {
        const hasCompany = Boolean(currentUser?.company?._id || currentUser?.company);
        if (hasCompany) {
          setProperties([]);
          return;
        }
        const userId = currentUser?._id || currentUser?.id;
        const mockPropsWithHost = mockProperties.map(prop => ({
          ...prop,
          hostId: userId,
          host: {
            _id: userId,
            id: userId,
            name: currentUser?.name,
            email: currentUser?.email
          }
        }));
        setProperties(mockPropsWithHost);
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
      if (!isMountedRef.current) return;
      const hasCompany = Boolean(currentUser?.company?._id || currentUser?.company);
      if (hasCompany) {
        setProperties([]);
        return;
      }
      const userId = currentUser?._id || currentUser?.id;
      const mockPropsWithHost = mockProperties.map(prop => ({
        ...prop,
        hostId: userId,
        host: {
          _id: userId,
          id: userId,
          name: currentUser?.name,
          email: currentUser?.email
        }
      }));
      setProperties(mockPropsWithHost);
    }
  };

  const fetchMeetingRooms = async (userOverride = null) => {
    try {
      const token = localStorage.getItem('token');
      const user = userOverride || currentUser;
      const userId = String(user?._id || user?.id || '');
      const userRole = String(user?.role || '');
      const isCompanyScopedUser =
        Boolean(user?.company?._id || user?.company) && COMPANY_ROLES.has(userRole);
      if (!userId) {
        setMeetingRooms([]);
        return;
      }

      let apiRooms = [];
      try {
        const response = await fetch(getApiUrl('/api/meetings/rooms?ownerAddedOnly=true&managedOnly=true'), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const result = await response.json();
        apiRooms = Array.isArray(result) ? result : [];
      } catch (apiError) {
        console.error('Error fetching meeting rooms from API:', apiError);
      }

      const scopedApiRooms = isCompanyScopedUser ? apiRooms : apiRooms.filter((room) => {
        const owner = room?.userId || room?.hostId;
        if (!owner) return false;
        if (typeof owner === 'string') return owner === userId;
        if (typeof owner === 'object') {
          const ownerId = owner._id || owner.id || owner.toString?.();
          return String(ownerId) === userId;
        }
        return false;
      });

      const localRooms = JSON.parse(localStorage.getItem('meetingRooms') || '[]');
      const ownLocalRooms = localRooms.filter((room) => {
        if (isCompanyScopedUser) return false;
        const ownerId = room?.hostId || room?.userId;
        return ownerId ? String(ownerId) === userId : false;
      });

      const mergedById = new Map();
      [...scopedApiRooms, ...ownLocalRooms].forEach((room, index) => {
        const key = String(room?._id || room?.id || `${room?.name || room?.roomName || 'room'}-${index}`);
        if (!mergedById.has(key)) {
          mergedById.set(key, room);
        }
      });

      if (!isMountedRef.current) return;
      setMeetingRooms(Array.from(mergedById.values()));
    } catch (error) {
      console.error('Error fetching meeting rooms:', error);
      if (isMountedRef.current) {
        setMeetingRooms([]);
      }
    }
  };

  const fetchEnquiries = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/api/bookings/host/enquiries'), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const result = await response.json();
      if (!isMountedRef.current) return;

      if (result.success) {
        const normalizeEnquiryStatus = (booking) => {
          const bookingType = booking?.bookingType || (booking?.room ? 'meeting' : 'stay');
          const rawStatus = String(booking?.status || 'pending').toLowerCase();
          const hostDecision = String(booking?.hostDecision || '').toLowerCase();

          if (bookingType !== 'meeting') return rawStatus;

          // Meeting bookings are often created as "confirmed" by schedule flow.
          // For host enquiries, pending decision should still appear as pending.
          if (hostDecision === 'approved') return 'confirmed';
          if (hostDecision === 'declined') return 'declined';
          if (['declined', 'cancelled', 'completed'].includes(rawStatus)) return rawStatus;
          return 'pending';
        };

        const transformedEnquiries = result.bookings.map((booking) => {
          const staffDetails = booking.staffDetails || booking.user?.staffProfile || null;
          const companySnapshot = booking.companySnapshot || null;
          const bookedByName = String(staffDetails?.name || booking.user?.name || 'Guest').trim() || 'Guest';
          const companyName = String(
            companySnapshot?.name ||
            booking.user?.company?.profile?.displayName ||
            booking.user?.company?.name ||
            ''
          ).trim();

          return {
            id: booking._id,
            guestId: booking.user?._id || booking.user,
            guestName: bookedByName,
            guestEmail: booking.user?.email || '',
            bookingType: booking.bookingType || (booking.room ? 'meeting' : 'stay'),
            propertyName: booking.property?.name || booking.room?.name || 'Property',
            meetingRoomId: booking.room?._id || null,
            checkIn: booking.checkIn || booking.startTime,
            checkOut: booking.checkOut || booking.endTime,
            guests: booking.bookingType === 'meeting'
              ? (booking.attendees || 1)
              : ((booking.guests?.adults || 0) + (booking.guests?.children || 0)),
            arrivalTime: booking.arrivalTime || '',
            message: booking.specialRequests || booking.notes || '',
            status: normalizeEnquiryStatus(booking),
            hostDecision: booking.hostDecision || null,
            hostDecisionAt: booking.hostDecisionAt || null,
            createdAt: booking.createdAt,
            totalAmount: booking.totalPrice || 0,
            cancelReason: booking.cancelReason || null,
            canceledBy: booking.canceledBy || null,
            cancelledAt: booking.cancelledAt || null,
            staffDetails,
            companySnapshot,
            companyName
          };
        });
        setEnquiries(transformedEnquiries);
        console.log('✅ Loaded', transformedEnquiries.length, 'enquiries');
      } else {
        setEnquiries([]);
      }
    } catch (error) {
      console.error('Error fetching enquiries:', error);
      if (isMountedRef.current) setEnquiries([]);
    }
  };

  const fetchHostEarningsAndPayouts = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setEarningsLoading(true);
    try {
      const [earningsRes, payoutsRes] = await Promise.all([
        fetch(getApiUrl('/api/payments/host/earnings'), { headers: { Authorization: `Bearer ${token}` } }),
        fetch(getApiUrl('/api/payments/host/payouts'), { headers: { Authorization: `Bearer ${token}` } })
      ]);
      const earningsData = await earningsRes.json();
      const payoutsData = await payoutsRes.json();
      if (isMountedRef.current) {
        if (earningsData.success) setHostEarnings({ payments: earningsData.payments || [], summary: earningsData.summary || { totalEarned: 0, totalCommission: 0, netBalance: 0 } });
        if (payoutsData.success) setHostPayouts(payoutsData.payouts || []);
      }
    } catch (e) {
      if (isMountedRef.current) {
        setHostEarnings({ payments: [], summary: { totalEarned: 0, totalCommission: 0, netBalance: 0 } });
        setHostPayouts([]);
      }
    } finally {
      if (isMountedRef.current) setEarningsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'earnings' && currentUser) fetchHostEarningsAndPayouts();
  }, [activeTab, currentUser]);

  useEffect(() => {
    if (activeTab === 'properties' && currentUser) {
      fetchProperties();
      fetchMeetingRooms(currentUser);
    }
  }, [activeTab, currentUser]);

  const handlePropertyAdded = (newProperty) => {
    // Add current host's ID to the property
    const userId = currentUser?._id || currentUser?.id;
    const propertyWithHost = {
      ...newProperty,
      hostId: userId,
      host: {
        _id: userId,
        id: userId,
        name: currentUser?.name,
        email: currentUser?.email
      }
    };
    
    console.log('✅ Property added with hostId:', userId);
    console.log('📦 Property:', propertyWithHost);
    
    setProperties(prev => [propertyWithHost, ...prev]);
  };

  const handleMeetingRoomAdded = (newRoom) => {
    console.log('Meeting room added:', newRoom);
    setListingView('meeting');
    fetchMeetingRooms(currentUser);
    // Trigger custom event for same-tab updates
    window.dispatchEvent(new Event('meetingRoomsUpdated'));
    toast.success('Meeting room added successfully!');
  };

  useEffect(() => {
    if (!currentUser) return undefined;
    const refreshMeetingRooms = () => fetchMeetingRooms();
    window.addEventListener('meetingRoomsUpdated', refreshMeetingRooms);
    return () => window.removeEventListener('meetingRoomsUpdated', refreshMeetingRooms);
  }, [currentUser]);

  const openPropertyTypeModal = () => {
    setShowListingTypePicker(true);
    setShowPropertyTypeModal(true);
  };

  const handleSelectStayProperty = () => {
    setShowListingTypePicker(false);
    setShowPropertyTypeModal(false);
    navigate('/host/add-property');
  };

  const handleSelectMeetingRoom = () => {
    setShowListingTypePicker(false);
    setShowPropertyTypeModal(false);
    setShowMeetingRoomForm(true);
  };

  const handleDeleteProperty = (propertyId) => {
    setPropertyToDelete(propertyId);
  };

  const handleDeletePropertyCancel = () => {
    setPropertyToDelete(null);
  };

  const handleDeletePropertyConfirm = async () => {
    if (!canManageListingsForUser(currentUser)) {
      toast.info('Your role has read-only access to listings.');
      return;
    }
    const propertyId = propertyToDelete;
    if (!propertyId) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/api/properties/${propertyId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (result.success) {
        setProperties(prev => prev.filter(p => String(p._id || p.id) !== String(propertyId)));
        toast.success('Property deleted successfully!');
        setPropertyToDelete(null);
      } else {
        toast.error('Failed to delete property: ' + result.message);
      }
    } catch (error) {
      console.error('Error deleting property:', error);
      toast.error('Failed to delete property');
    }
  };

  const handleEditProperty = (property) => {
    if (!canManageListingsForUser(currentUser)) {
      toast.info('Your role has read-only access to listings.');
      return;
    }
    setEditingProperty(property);
    setShowEditPropertyModal(true);
  };

  const handlePropertyUpdated = (updatedProperty) => {
    setProperties(prev => prev.map(p => 
      (p._id || p.id) === (updatedProperty._id || updatedProperty.id) 
        ? updatedProperty 
        : p
    ));
    setShowEditPropertyModal(false);
    setEditingProperty(null);
  };

  const handleToggleStatus = async (propertyId) => {
    if (!canManageListingsForUser(currentUser)) {
      toast.info('Your role has read-only access to listings.');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/api/properties/${propertyId}/toggle-status`), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (result.success) {
        setProperties(prev => prev.map(p => 
          p._id === propertyId ? result.property : p
        ));
      } else {
        toast.error('Failed to update status: ' + result.message);
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleSignInClick = () => {
    // Set redirect path to /host for proper navigation after login
    localStorage.setItem('authRedirectPath', '/host');
    setShowLoginModal(true);
  };

  const handleOnboardingComplete = async (hostData) => {
    console.log('Host onboarding completed:', hostData);

    try {
      const token = localStorage.getItem('token');

      // Upload profile photo first if present (host profile save)
      const profilePhoto = hostData?.personalInfo?.profilePhoto;
      if (profilePhoto && typeof profilePhoto === 'object' && profilePhoto instanceof File) {
        const formData = new FormData();
        formData.append('profilePicture', profilePhoto);
        await fetch(getApiUrl('/api/auth/upload-profile-picture'), {
          method: 'PATCH',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData,
        });
      }

      const response = await fetch(getApiUrl('/api/auth/complete-host-onboarding'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ hostData }),
      });

      const result = await response.json();
      if (!isMountedRef.current) return;

      if (result.success) {
        localStorage.setItem('user', JSON.stringify(result.user));
        localStorage.removeItem('hostProfileComplete');
        setCurrentUser(result.user);
        setProfileData(prev => getProfileDataFromUser(result.user, prev));
        setIsHostProfileComplete(true);
        setShowOnboarding(false);
        fetchProperties();
        fetchMeetingRooms(result.user);
        fetchEnquiries();
        if (!hostData.fromStep1Save) {
          if (hostData.listingType === 'stay') setShowAddPropertyModal(true);
          else if (hostData.listingType === 'meeting') setShowMeetingRoomForm(true);
        }
      } else {
        toast.error(result.message || 'Failed to save host profile. Please check your details.');
      }
    } catch (error) {
      console.error('Error completing host onboarding:', error);
      if (!isMountedRef.current) return;
      toast.error('Failed to save host profile. Please try again.');
    }
  };

  const handleEnquiryAction = async (enquiryId, action) => {
    // Find the enquiry
    const enquiry = enquiries.find(e => e.id === enquiryId);
    if (!enquiry) {
      console.error('Enquiry not found:', enquiryId);
      return;
    }

    // Prevent double-click / multiple submissions
    if (enquiryActionLoading[enquiryId]) return;
    setEnquiryActionLoading(prev => ({ ...prev, [enquiryId]: action }));

    try {
      // Get current user/host ID
      const hostId = currentUser?._id || currentUser?.id;
      const hostName = currentUser?.name || currentUser?.email || 'Host';

      // Call backend API to update booking status
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/api/bookings/${enquiryId}/status`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          status: action,
          hostId: hostId 
        }),
      });

      const result = await response.json();

      if (result.success) {
        // Update local state immediately
        setEnquiries(prev => 
          prev.map(e => 
            e.id === enquiryId 
              ? {
                ...e,
                status: action === 'approved' ? 'confirmed' : action,
                hostDecision: e.bookingType === 'meeting' ? action : (e.hostDecision || null),
                hostDecisionAt: e.bookingType === 'meeting' ? new Date().toISOString() : (e.hostDecisionAt || null)
              }
              : e
          )
        );

        // Show success message (server already creates host notification - no need to call sendHostActionConfirmationNotification)
        const actionText = action === 'approved' ? 'approved' : 'declined';
        toast.success(`Booking ${actionText} successfully! Guest has been notified via email and app notification.`);

        // Reload notifications to show the confirmation (created by server)
        setTimeout(() => {
          loadNotificationsAndMessages(currentUser || { _id: hostId, id: hostId });
        }, 500);

        // Refresh enquiries list to get updated data from server
        setTimeout(() => {
          fetchEnquiries();
        }, 1000);
      } else {
        toast.error('Failed to update booking: ' + result.message);
      }
    } catch (error) {
      console.error('❌ Error updating booking:', error);
      toast.error('Failed to update booking. Please try again.');
    } finally {
      setEnquiryActionLoading(prev => {
        const next = { ...prev };
        delete next[enquiryId];
        return next;
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#f59e0b';
      case 'approved': return '#10b981';
      case 'confirmed': return '#10b981';
      case 'declined': return '#ef4444';
      case 'cancelled': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const calculateNights = (checkIn, checkOut) => {
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  };

  const getEnquiryBookedByLabel = (enquiry) => {
    const staffName = String(enquiry?.staffDetails?.name || '').trim();
    const staffRole = String(enquiry?.staffDetails?.role || '').trim();
    if (!staffName) return String(enquiry?.guestName || 'Guest').trim() || 'Guest';
    return staffRole ? `${staffName} (${staffRole})` : staffName;
  };

  const getEnquiryCompanyName = (enquiry) => {
    return String(enquiry?.companyName || enquiry?.companySnapshot?.name || '').trim();
  };

  // Host should respond only once. Meeting bookings use persisted hostDecision.
  const canRespondToEnquiry = (enquiry) => {
    const status = String(enquiry?.status || '').toLowerCase();
    const hostDecision = String(enquiry?.hostDecision || '').toLowerCase();

    // Treat all final statuses as non-actionable, including "confirmed"
    // (meeting approvals are often persisted as confirmed by backend).
    if (['approved', 'confirmed', 'declined', 'cancelled', 'completed'].includes(status)) return false;
    if (enquiry?.bookingType === 'meeting') return !['approved', 'confirmed', 'declined'].includes(hostDecision);
    return status === 'pending';
  };

  // Filter and sort enquiries
  const getFilteredAndSortedEnquiries = () => {
    let filtered = [...enquiries];

    // Apply listing-type filter (from notification click)
    if (enquiryTypeFilter === 'meeting') {
      filtered = filtered.filter(e => e.bookingType === 'meeting');
    } else if (enquiryTypeFilter === 'hotel') {
      filtered = filtered.filter(e => e.bookingType !== 'meeting');
    }

    // Apply status filter
    if (enquiryFilter !== 'all') {
      if (enquiryFilter === 'approved') {
        // Show both 'approved' and 'confirmed' statuses
        filtered = filtered.filter(e => e.status === 'approved' || e.status === 'confirmed');
      } else {
        filtered = filtered.filter(e => e.status === enquiryFilter);
      }
    }

    // Apply search filter
    if (enquirySearchTerm) {
      const searchLower = enquirySearchTerm.toLowerCase();
      filtered = filtered.filter(e => 
        e.guestName.toLowerCase().includes(searchLower) ||
        e.guestEmail.toLowerCase().includes(searchLower) ||
        e.propertyName.toLowerCase().includes(searchLower) ||
        e.id.toString().includes(searchLower) ||
        getEnquiryCompanyName(e).toLowerCase().includes(searchLower) ||
        String(e.staffDetails?.employeeId || '').toLowerCase().includes(searchLower)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (enquirySortBy) {
        case 'date-desc':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'date-asc':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'amount-desc':
          return b.totalAmount - a.totalAmount;
        case 'amount-asc':
          return a.totalAmount - b.totalAmount;
        default:
          return 0;
      }
    });

    return filtered;
  };

  // Handle bulk selection
  const handleSelectEnquiry = (enquiryId) => {
    setSelectedEnquiries(prev => {
      if (prev.includes(enquiryId)) {
        return prev.filter(id => id !== enquiryId);
      } else {
        return [...prev, enquiryId];
      }
    });
  };

  const handleSelectAllEnquiries = () => {
    const filteredEnquiries = getFilteredAndSortedEnquiries();
    const pendingIds = filteredEnquiries
      .filter(e => e.status === 'pending')
      .map(e => e.id);
    
    if (selectedEnquiries.length === pendingIds.length) {
      setSelectedEnquiries([]);
    } else {
      setSelectedEnquiries(pendingIds);
    }
  };

  const closeBookingActionConfirm = () => {
    if (bookingConfirmLoading) return;
    setBookingActionConfirm(null);
  };

  const executeBulkAction = async (action) => {
    let successCount = 0;
    let failCount = 0;

    for (const enquiryId of selectedEnquiries) {
      try {
        await handleEnquiryAction(enquiryId, action);
        successCount++;
      } catch (error) {
        console.error('Error processing enquiry:', enquiryId, error);
        failCount++;
      }
    }

    toast.success(`Processed ${successCount} booking(s) successfully${failCount > 0 ? `, ${failCount} failed` : ''}`);
    setSelectedEnquiries([]);
    setShowBulkActions(false);
  };

  // Handle bulk actions
  const handleBulkAction = (action) => {
    if (selectedEnquiries.length === 0) {
      toast.warning('Please select at least one enquiry');
      return;
    }

    const isApprove = action === 'approved';
    setBookingActionConfirm({
      variant: isApprove ? 'approve' : 'decline',
      title: isApprove
        ? `Approve ${selectedEnquiries.length} booking(s)?`
        : `Decline ${selectedEnquiries.length} booking(s)?`,
      message: isApprove
        ? 'The selected guests will be notified that their bookings are approved.'
        : 'The selected guests will be notified that their booking requests were declined.',
      confirmText: isApprove ? 'Approve All' : 'Decline All',
      onConfirm: async () => {
        setBookingConfirmLoading(true);
        try {
          await executeBulkAction(action);
          setBookingActionConfirm(null);
        } finally {
          setBookingConfirmLoading(false);
        }
      },
    });
  };

  // Export enquiries to CSV
  const handleExportEnquiries = () => {
    const filtered = getFilteredAndSortedEnquiries();
    
    const csvContent = [
      ['Booking ID', 'Guest Name', 'Guest Email', 'Property', 'Check-in', 'Check-out', 'Guests', 'Amount', 'Status', 'Date Received'].join(','),
      ...filtered.map(e => [
        e.id,
        e.guestName,
        e.guestEmail,
        e.propertyName,
        formatDate(e.checkIn),
        formatDate(e.checkOut),
        e.guests,
        e.totalAmount,
        e.status,
        formatDate(e.createdAt)
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `enquiries-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleProfileEdit = () => {
    setIsEditingProfile(true);
  };

  const handleProfileSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/api/auth/update-profile'), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(profileData),
      });

      const result = await response.json();

      if (result.success) {
        const updatedUser = result.user
          ? { ...currentUser, ...result.user, hostProfile: result.user.hostProfile ?? currentUser?.hostProfile }
          : { ...currentUser, ...profileData, hostProfile: { ...(currentUser?.hostProfile || {}), ...profileData } };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setCurrentUser(updatedUser);
        setIsEditingProfile(false);
        toast.success('Profile updated successfully!');
      } else {
        toast.error('Failed to update profile: ' + result.message);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      const updatedUser = {
        ...currentUser,
        ...profileData,
        hostProfile: {
          ...(currentUser?.hostProfile || {}),
          address: profileData.address,
          city: profileData.city,
          state: profileData.state,
          pincode: profileData.pincode,
          dateOfBirth: profileData.dateOfBirth,
          emergencyContact: profileData.emergencyContact,
          governmentId: profileData.governmentId,
          governmentIdNumber: profileData.governmentIdNumber
        }
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setCurrentUser(updatedUser);
      setIsEditingProfile(false);
      toast.info('Profile updated locally!');
    }
  };

  const handleProfileCancel = () => {
    // Reset to current user data (including hostProfile from onboarding)
    setProfileData(prev => getProfileDataFromUser(currentUser, prev));
    setIsEditingProfile(false);
  };

  const handleProfileChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddInterest = () => {
    const newInterest = prompt('Enter new interest:');
    if (newInterest && newInterest.trim()) {
      setProfileData(prev => ({
        ...prev,
        interests: [...prev.interests, newInterest.trim()]
      }));
    }
  };

  const handleRemoveInterest = (index) => {
    setProfileData(prev => ({
      ...prev,
      interests: prev.interests.filter((_, i) => i !== index)
    }));
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
    setShowMobileMenu(false);
  };

  const handleLogoutConfirm = () => {
    try {
      const user = localStorage.getItem('user');
      if (user) {
        const userObj = JSON.parse(user);
        const userId = userObj._id || userObj.id;
        localStorage.removeItem(`officeSpaceAnswered_${userId}`);
        localStorage.removeItem(`meetingHomePopupShown_${userId}`);
      }
    } catch (error) {
      console.error('Error clearing user-specific data:', error);
    }
    
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('hostProfileComplete');
    localStorage.removeItem('meetingHomePopupShown');
    
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('officeSpaceAnswered_') || key.startsWith('user_') || key.startsWith('booking_') || key.startsWith('lastSearch_') || key.startsWith('recentSearches_') || key.startsWith('meetingHomePopupShown_')) {
        localStorage.removeItem(key);
      }
    });
    
    window.location.href = '/';
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  const handleViewMeetingRoom = (room) => {
    const roomId = room?._id || room?.id;
    if (!roomId) {
      toast.error('Room ID not found');
      return;
    }
    navigate(`/meetings/room/${roomId}`);
  };

  const handleEditMeetingRoom = (room) => {
    if (!canManageListingsForUser(currentUser)) {
      toast.info('Your role has read-only access to listings.');
      return;
    }
    const roomId = room?._id || room?.id;
    if (!roomId) {
      toast.error('Room ID not found');
      return;
    }
    navigate(`/meetings/room/${roomId}`);
  };

  const handleToggleMeetingRoom = (roomId) => {
    if (!canManageListingsForUser(currentUser)) {
      toast.info('Your role has read-only access to listings.');
      return;
    }
    setMeetingRooms((prev) =>
      prev.map((room) => {
        const id = String(room?._id || room?.id);
        return id === String(roomId) ? { ...room, isActive: room.isActive === false ? true : false } : room;
      })
    );

    // Keep local fallback data in sync if room exists there
    const localRooms = JSON.parse(localStorage.getItem('meetingRooms') || '[]');
    const updatedLocalRooms = localRooms.map((room) => {
      const id = String(room?._id || room?.id);
      return id === String(roomId) ? { ...room, isActive: room.isActive === false ? true : false } : room;
    });
    localStorage.setItem('meetingRooms', JSON.stringify(updatedLocalRooms));
    window.dispatchEvent(new Event('meetingRoomsUpdated'));
    toast.success('Meeting room status updated');
  };

  const handleDeleteMeetingRoom = (room) => {
    if (!canManageListingsForUser(currentUser)) {
      toast.info('Your role has read-only access to listings.');
      return;
    }
    const roomId = room?._id || room?.id;
    if (!roomId) {
      toast.error('Room ID not found');
      return;
    }
    setMeetingRoomToDelete({
      id: String(roomId),
      name: room?.name || room?.roomName || 'this meeting room',
    });
    setShowMeetingRoomDeleteModal(true);
  };

  const handleDeleteMeetingRoomCancel = () => {
    setShowMeetingRoomDeleteModal(false);
    setMeetingRoomToDelete(null);
  };

  const handleDeleteMeetingRoomConfirm = async () => {
    if (!canManageListingsForUser(currentUser)) {
      toast.info('Your role has read-only access to listings.');
      return;
    }
    const roomId = meetingRoomToDelete?.id;
    if (!roomId) {
      handleDeleteMeetingRoomCancel();
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please log in again to delete meeting room');
        navigate('/login');
        return;
      }

      // Prevent stale-session deletes: ensure UI user matches token user
      const meResponse = await fetch(getApiUrl('/api/auth/me'), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const meResult = await meResponse.json().catch(() => ({}));
      if (!meResponse.ok || !meResult?.success) {
        toast.error(meResult?.message || 'Session expired. Please log in again.');
        localStorage.removeItem('token');
        navigate('/login');
        return;
      }

      const sessionUserId = String(meResult?.user?._id || meResult?.user?.id || '');
      const uiUserId = String(currentUser?._id || currentUser?.id || '');
      if (sessionUserId && uiUserId && sessionUserId !== uiUserId) {
        toast.error('Session mismatch detected. Please log in again.');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
        return;
      }

      const isMongoId = /^[a-fA-F0-9]{24}$/.test(roomId);

      if (isMongoId) {
        await roomsAPI.deleteRoom(roomId);
      }

      setMeetingRooms((prev) => prev.filter((room) => String(room?._id || room?.id) !== String(roomId)));

      // Remove from local fallback store if present
      const localRooms = JSON.parse(localStorage.getItem('meetingRooms') || '[]');
      const updatedLocalRooms = localRooms.filter((room) => String(room?._id || room?.id) !== String(roomId));
      localStorage.setItem('meetingRooms', JSON.stringify(updatedLocalRooms));
      window.dispatchEvent(new Event('meetingRoomsUpdated'));
      toast.success('Meeting room deleted');
      handleDeleteMeetingRoomCancel();
    } catch (error) {
      console.error('Error deleting meeting room:', error);
      const serverMessage = error?.response?.data?.message;
      toast.error(serverMessage || error.message || 'Failed to delete meeting room');
    }
  };

  const activeListings = listingView === 'meeting' ? meetingRooms : properties;
  const activeListingLabel = listingView === 'meeting' ? 'meeting room' : 'property';
  const isMeetingNotification = (notif) => {
    const notifType = String(notif?.type || '').toLowerCase();
    const bookingType = String(notif?.metadata?.bookingType || '').toLowerCase();
    const title = String(notif?.title || '').toLowerCase();
    const message = String(notif?.message || '').toLowerCase();
    const propertyName = String(notif?.propertyName || '').toLowerCase();

    if (notifType === 'meeting_room_booked') return true;
    if (bookingType === 'meeting') return true;
    if (title.includes('meeting room') || message.includes('meeting room')) return true;
    if (title.includes('conference') || message.includes('conference')) return true;
    if (propertyName.includes('boardroom') || propertyName.includes('conference')) return true;
    return false;
  };

  const hasRichMeetingMetadata = (notif) => {
    const meta = notif?.metadata || {};
    return Boolean(
      meta.roomName &&
      (meta.dateLabel || meta.meetingPurpose || meta.timeLabel || meta.bookedByName)
    );
  };

  const isHotelBookingNotification = (notif) => {
    if (isMeetingNotification(notif)) return false;
    const notifType = String(notif?.type || '').toLowerCase();
    const bookingType = String(notif?.metadata?.bookingType || '').toLowerCase();

    if (bookingType === 'stay' || bookingType === 'hotel') return true;
    return ['booking_request', 'booking_approved', 'booking_confirmed', 'booking_declined', 'booking_cancelled'].includes(notifType);
  };

  const meetingNotifications = notifications.filter(isMeetingNotification);
  const hotelNotifications = notifications.filter(isHotelBookingNotification);
  const filteredNotifications = notificationCategory === 'meeting' ? meetingNotifications : hotelNotifications;
  const companyInfo = currentUser?.company || null;
  const isCompanyUser = Boolean(companyInfo?._id || companyInfo);
  const isCompanyStaff = String(currentUser?.role || '') === 'company_staff';
  const canManageListings = canManageListingsForUser(currentUser);
  const companyThemeColor = getCompanyThemeColor(companyInfo);
  const companyName = companyInfo?.profile?.displayName || companyInfo?.name || 'Company';
  const companySupportEmail = companyInfo?.branding?.supportEmail || companyInfo?.profile?.contactEmail || '';
  const companySupportPhone = companyInfo?.branding?.supportPhone || companyInfo?.profile?.contactPhone || '';
  const totalListingsCount = properties.length + meetingRooms.length;
  const activeBookingsCount = enquiries.filter((enquiry) =>
    ['pending', 'approved', 'confirmed'].includes(String(enquiry?.status || '').toLowerCase())
  ).length;
  const dashboardRevenue = Number(
    hostEarnings?.summary?.netBalance ??
      enquiries
        .filter((enquiry) => ['approved', 'confirmed', 'completed'].includes(String(enquiry?.status || '').toLowerCase()))
        .reduce((sum, enquiry) => sum + Number(enquiry?.totalAmount || 0), 0)
  );

  if (loading) {
    return (
      <div className="host-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Show onboarding for first-time hosts or users who want to become hosts
  if (showOnboarding && !isHostProfileComplete) {
    return (
      <div className="host-page">
        <HostOnboarding 
          onComplete={handleOnboardingComplete}
          onClose={() => navigate('/')}
          currentUser={currentUser}
        />
      </div>
    );
  }

  // If user is already a host with completed profile, show dashboard directly
  if (currentUser && canAccessHostDashboard(currentUser) && (currentUser.hostProfileComplete || isCompanyUser)) {
    return (
      <div className="host-page" style={{ '--company-theme-color': companyThemeColor }}>
        {/* Custom Header */}
        <header className="host-header">
          <div className="host-header-left">
            <h1>Hi, {currentUser.name?.split(' ')[0] || currentUser.email?.split('@')[0]} 👋</h1>
            <p>{isCompanyUser ? `${companyName} Host Dashboard` : 'Manage your properties'}</p>
            {isCompanyUser && (
              <div className="company-brand-chip">
                {companyInfo?.branding?.logoUrl && (
                  <img
                    src={companyInfo.branding.logoUrl}
                    alt={`${companyName} logo`}
                    className="company-brand-logo"
                  />
                )}
                <span>{companyName}</span>
                {companySupportEmail && <span>{companySupportEmail}</span>}
                {companySupportPhone && <span>{companySupportPhone}</span>}
              </div>
            )}
          </div>
          
          <div className="host-header-right">
            <button 
              className="header-btn guest-view-btn"
              onClick={() => navigate('/')}
              title="Switch to Guest View"
            >
              <FaEye />
              <span className="btn-text">Guest View</span>
            </button>
            
            <button 
              className="header-btn notification-btn"
              onClick={() => setActiveTab('notifications')}
              title="Notifications"
            >
              <FaBell />
              {unreadNotifCount > 0 && (
                <span className="header-badge">{unreadNotifCount}</span>
              )}
            </button>

            <button 
              className="header-btn messages-btn"
              onClick={() => navigate('/host/messages')}
              title="Messages"
            >
              <FaComments />
              {unreadMessageCount > 0 && (
                <span className="header-badge">{unreadMessageCount}</span>
              )}
            </button>
            
            <button 
              className="header-btn profile-btn"
              onClick={() => setActiveTab('profile')}
              title="Profile"
            >
              <FaUser />
            </button>
            
            <button 
              className="header-btn menu-btn"
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              title="Menu"
            >
              {showMobileMenu ? <FaTimes /> : <FaBars />}
            </button>
          </div>

          {/* Menu Dropdown */}
          {showMobileMenu && (
            <div className="header-menu-dropdown">
              <button 
                className={`menu-dropdown-item ${activeTab === 'dashboard' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('dashboard');
                  setShowMobileMenu(false);
                }}
              >
                <FaHome />
                <span>Dashboard</span>
              </button>
              <button 
                className={`menu-dropdown-item ${activeTab === 'enquiries' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('enquiries');
                  setShowMobileMenu(false);
                }}
              >
                <FaEnvelope />
                <span>Enquiries</span>
                {enquiries.filter(e => e.status === 'pending').length > 0 && (
                  <span className="dropdown-badge">
                    {enquiries.filter(e => e.status === 'pending').length}
                  </span>
                )}
              </button>
              <button 
                className={`menu-dropdown-item ${activeTab === 'notifications' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('notifications');
                  setShowMobileMenu(false);
                }}
              >
                <FaBell />
                <span>Notifications</span>
                {unreadNotifCount > 0 && (
                  <span className="dropdown-badge">{unreadNotifCount}</span>
                )}
              </button>
              <button 
                className={`menu-dropdown-item ${activeTab === 'messages' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('messages');
                  setShowMobileMenu(false);
                }}
              >
                <FaComments />
                <span>Messages</span>
                {unreadMessageCount > 0 && (
                  <span className="dropdown-badge">{unreadMessageCount}</span>
                )}
              </button>
              <button 
                className={`menu-dropdown-item ${activeTab === 'properties' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('properties');
                  setShowMobileMenu(false);
                }}
              >
                <FaHome />
                <span>Properties</span>
              </button>
              <button 
                className={`menu-dropdown-item ${activeTab === 'earnings' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('earnings');
                  setShowMobileMenu(false);
                }}
              >
                <FaRupeeSign />
                <span>Earnings & Payouts</span>
              </button>
              <button 
                className={`menu-dropdown-item ${activeTab === 'calendar' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('calendar');
                  setShowMobileMenu(false);
                }}
              >
                <FaCalendarAlt />
                <span>Calendar</span>
              </button>
              <button 
                className={`menu-dropdown-item ${activeTab === 'profile' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('profile');
                  setShowMobileMenu(false);
                }}
              >
                <FaUser />
                <span>Profile</span>
              </button>
              <button 
                className={`menu-dropdown-item ${activeTab === 'settings' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('settings');
                  setShowMobileMenu(false);
                }}
              >
                <FaCog />
                <span>Settings</span>
              </button>
              <div className="menu-divider"></div>
              <button 
                className="menu-dropdown-item logout-item"
                onClick={handleLogoutClick}
              >
                <FaSignOutAlt />
                <span>Logout</span>
              </button>
            </div>
          )}
        </header>

        {/* Logout Confirmation Modal */}
        {showLogoutModal && (
          <div className="logout-modal-overlay" onClick={handleLogoutCancel}>
            <div className="logout-modal-container" onClick={(e) => e.stopPropagation()}>
              <div className="logout-modal-icon">
                <FaSignOutAlt />
              </div>
              <h2>Confirm Logout</h2>
              <p>Are you sure you want to logout? You'll need to sign in again to access your host dashboard.</p>
              <div className="logout-modal-actions">
                <button className="btn-logout-cancel" onClick={handleLogoutCancel}>
                  <FaTimes />
                  <span>Cancel</span>
                </button>
                <button className="btn-logout-confirm" onClick={handleLogoutConfirm}>
                  <FaSignOutAlt />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        )}

        <DeleteConfirmModal
          isOpen={Boolean(propertyToDelete)}
          onClose={handleDeletePropertyCancel}
          onConfirm={handleDeletePropertyConfirm}
        />

        <BookingActionConfirmModal
          isOpen={Boolean(bookingActionConfirm)}
          onClose={closeBookingActionConfirm}
          onConfirm={() => bookingActionConfirm?.onConfirm?.()}
          variant={bookingActionConfirm?.variant || 'approve'}
          title={bookingActionConfirm?.title || ''}
          message={bookingActionConfirm?.message || ''}
          details={bookingActionConfirm?.details || []}
          confirmText={bookingActionConfirm?.confirmText || 'Confirm'}
          loading={bookingConfirmLoading}
        />

        {showMeetingRoomDeleteModal && (
          <div className="delete-room-modal-overlay" onClick={handleDeleteMeetingRoomCancel}>
            <div className="delete-room-modal-container" onClick={(e) => e.stopPropagation()}>
              <div className="delete-room-modal-icon">
                <FaTrash />
              </div>
              <h2>Delete Meeting Room?</h2>
              <p>
                This action will remove <strong>{meetingRoomToDelete?.name}</strong> from your host listings.
                You can’t undo this action.
              </p>
              <div className="delete-room-modal-actions">
                <button className="btn-delete-room-cancel" onClick={handleDeleteMeetingRoomCancel}>
                  <FaTimes />
                  <span>Cancel</span>
                </button>
                <button className="btn-delete-room-confirm" onClick={handleDeleteMeetingRoomConfirm}>
                  <FaTrash />
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </div>
        )}

        <main className="host-dashboard">
          <div className="dashboard-content-full">
            
            <AnimatePresence mode="wait">
              {activeTab === 'dashboard' && (
                <motion.div
                  key="dashboard"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="dashboard-overview"
                >
                  {/* Quick Stats */}
                  <div className="quick-stats">
                    <div className="stat-box">
                      <div className="stat-icon-wrapper blue">
                        <FaHome />
                      </div>
                      <div className="stat-content">
                        <span className="stat-label">Total Listings</span>
                        <span className="stat-value">{totalListingsCount}</span>
                      </div>
                    </div>
                    <div className="stat-box">
                      <div className="stat-icon-wrapper green">
                        <FaCalendarAlt />
                      </div>
                      <div className="stat-content">
                        <span className="stat-label">Active Bookings</span>
                        <span className="stat-value">{activeBookingsCount}</span>
                      </div>
                    </div>
                    <div className="stat-box">
                      <div className="stat-icon-wrapper orange">
                        <FaRupeeSign />
                      </div>
                      <div className="stat-content">
                        <span className="stat-label">Revenue</span>
                        <span className="stat-value">₹{dashboardRevenue.toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="stat-box">
                      <div className="stat-icon-wrapper purple">
                        <FaBell />
                      </div>
                      <div className="stat-content">
                        <span className="stat-label">Notifications</span>
                        <span className="stat-value">{unreadNotifCount}</span>
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="dashboard-section">
                    <h2 className="section-title">Quick Actions</h2>
                    <div className="quick-actions-grid">
                      <button className="action-card" onClick={openPropertyTypeModal}>
                        <div className="action-icon">
                          <FaPlus />
                        </div>
                        <span>Add Property</span>
                      </button>
                      <button className="action-card" onClick={() => setActiveTab('enquiries')}>
                        <div className="action-icon">
                          <FaEnvelope />
                        </div>
                        <span>View Enquiries</span>
                      </button>
                      <button className="action-card" onClick={() => setActiveTab('properties')}>
                        <div className="action-icon">
                          <FaHome />
                        </div>
                        <span>Manage Properties</span>
                      </button>
                      <button className="action-card" onClick={() => setActiveTab('messages')}>
                        <div className="action-icon">
                          <FaComments />
                        </div>
                        <span>Messages</span>
                      </button>
                    </div>
                    {showListingTypePicker && (
                      <div className="listing-type-picker" role="group" aria-label="Choose listing type">
                        <button
                          type="button"
                          className="listing-type-picker-btn listing-type-picker-btn-hotel"
                          onClick={handleSelectStayProperty}
                        >
                          <FaHome aria-hidden="true" />
                          <span>Add Hotel Room</span>
                        </button>
                        <button
                          type="button"
                          className="listing-type-picker-btn listing-type-picker-btn-meeting"
                          onClick={handleSelectMeetingRoom}
                        >
                          <FaCalendarAlt aria-hidden="true" />
                          <span>Add Meeting Room</span>
                        </button>
                      </div>
                    )}
                    {isCompanyStaff && !canManageListings && (
                      <p className="role-readonly-note">
                        Your current role is read-only for listings. Contact a company admin or manager to make changes.
                      </p>
                    )}
                  </div>

                  {/* Recent Activity */}
                  {enquiries.length > 0 && (
                    <div className="dashboard-section">
                      <div className="section-header-row">
                        <h2 className="section-title">Recent Enquiries</h2>
                        <button className="view-all-link" onClick={() => setActiveTab('enquiries')}>
                          View All
                        </button>
                      </div>
                      <div className="activity-list">
                        {enquiries.slice(0, 3).map(enquiry => (
                          <div key={enquiry.id} className="activity-item">
                            <div className="activity-avatar">
                              <FaUser />
                            </div>
                            <div className="activity-details">
                              <div className="activity-main">
                                <span className="activity-name">{enquiry.guestName}</span>
                                <span className="activity-property">{enquiry.propertyName}</span>
                              </div>
                              <div className="activity-meta">
                                <span>{formatDate(enquiry.checkIn)} - {formatDate(enquiry.checkOut)}</span>
                                <span>•</span>
                                <span>{enquiry.guests} guests</span>
                              </div>
                            </div>
                            <div className={`activity-status status-${enquiry.status}`}>
                              {enquiry.status}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Properties Overview */}
                  <div className="dashboard-section">
                    <div className="section-header-row">
                      <h2 className="section-title">Your Properties</h2>
                      <button className="view-all-link" onClick={() => setActiveTab('properties')}>
                        View All
                      </button>
                    </div>

                    {properties.length > 0 ? (
                      <div className="properties-preview">
                        {properties.slice(0, 3).map(property => (
                          <div key={property._id || property.id} className="property-preview-card">
                            <img 
                              src={getPropertyImage(property, 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=400')} 
                              alt={property.name}
                              className="property-preview-img"
                            />
                            <div className="property-preview-info">
                              <h4>{property.name}</h4>
                              <p className="property-preview-location">
                                <FaMapMarkerAlt />
                                {property.city}
                              </p>
                              <div className="property-preview-footer">
                                <span className={`property-preview-status ${property.status}`}>
                                  {property.status}
                                </span>
                                <span className="property-preview-price">
                                  ₹{property.pricePerNight?.toLocaleString()}/night
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="properties-preview-empty">
                        <p>No properties added yet.</p>
                        <button className="view-all-link" onClick={() => setActiveTab('properties')}>
                          Add Property
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Empty State for New Hosts */}
                  {properties.length === 0 && enquiries.length === 0 && (
                    <div className="dashboard-empty-state">
                      <div className="empty-state-icon">
                        <FaHome />
                      </div>
                      <h3>Welcome to Your Dashboard!</h3>
                      <p>Start by adding your first property to begin hosting</p>
                      <button
                        className="btn-primary"
                        onClick={openPropertyTypeModal}
                      >
                        <FaPlus />
                        Add Your First Property
                      </button>
                    </div>
                  )}
                </motion.div>
              )}

              {activeTab === 'enquiries' && (
                <motion.div
                  key="enquiries"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="enquiries-section"
                >
                  <div className="section-header">
                    <div>
                      <h1>Guest Enquiries</h1>
                      <p>Manage booking requests from potential guests</p>
                    </div>
                    <div className="header-actions">
                      <button 
                        className="btn-export-enquiries"
                        onClick={handleExportEnquiries}
                        title="Export to CSV"
                      >
                        <FaCalendarAlt />
                        <span>Export</span>
                      </button>
                      <button 
                        className="btn-refresh-enquiries"
                        onClick={() => {
                          console.log('🔄 Refreshing enquiries...');
                          fetchEnquiries();
                        }}
                        title="Refresh enquiries"
                      >
                        <FaBell />
                        <span>Refresh</span>
                      </button>
                    </div>
                  </div>

                  {/* Search and Filter Bar */}
                  {enquiryTypeFilter !== 'all' && (
                    <div className="enquiry-type-filter-indicator">
                      <span>
                        Showing only {enquiryTypeFilter === 'meeting' ? 'meeting room' : 'hotel room'} enquiries from notification click
                      </span>
                      <button
                        type="button"
                        className="btn-reset-enquiry-type-filter"
                        onClick={() => setEnquiryTypeFilter('all')}
                      >
                        Show all
                      </button>
                    </div>
                  )}

                  <div className="enquiries-toolbar">
                    <div className="search-box">
                      <input
                        type="text"
                        placeholder="Search by guest name, email, property, or booking ID..."
                        value={enquirySearchTerm}
                        onChange={(e) => setEnquirySearchTerm(e.target.value)}
                        className="search-input"
                      />
                    </div>
                    <div className="sort-dropdown">
                      <select
                        value={enquirySortBy}
                        onChange={(e) => setEnquirySortBy(e.target.value)}
                        className="sort-select"
                      >
                        <option value="date-desc">Newest First</option>
                        <option value="date-asc">Oldest First</option>
                        <option value="amount-desc">Highest Amount</option>
                        <option value="amount-asc">Lowest Amount</option>
                      </select>
                    </div>
                  </div>

                  {/* Filter Tabs */}
                  <div className="enquiries-filter-tabs">
                    <button 
                      className={`filter-tab ${enquiryFilter === 'all' ? 'active' : ''}`}
                      onClick={() => setEnquiryFilter('all')}
                    >
                      <span className="filter-label">All</span>
                      <span className="filter-count">{enquiries.length}</span>
                    </button>
                    <button 
                      className={`filter-tab ${enquiryFilter === 'pending' ? 'active' : ''}`}
                      onClick={() => setEnquiryFilter('pending')}
                    >
                      <span className="filter-label">Pending</span>
                      <span className="filter-count">{enquiries.filter(e => e.status === 'pending').length}</span>
                    </button>
                    <button 
                      className={`filter-tab ${enquiryFilter === 'approved' ? 'active' : ''}`}
                      onClick={() => setEnquiryFilter('approved')}
                    >
                      <span className="filter-label">Approved</span>
                      <span className="filter-count">{enquiries.filter(e => e.status === 'approved' || e.status === 'confirmed').length}</span>
                    </button>
                    <button 
                      className={`filter-tab ${enquiryFilter === 'declined' ? 'active' : ''}`}
                      onClick={() => setEnquiryFilter('declined')}
                    >
                      <span className="filter-label">Declined</span>
                      <span className="filter-count">{enquiries.filter(e => e.status === 'declined').length}</span>
                    </button>
                    <button 
                      className={`filter-tab ${enquiryFilter === 'cancelled' ? 'active' : ''}`}
                      onClick={() => setEnquiryFilter('cancelled')}
                    >
                      <span className="filter-label">Cancelled</span>
                      <span className="filter-count">{enquiries.filter(e => e.status === 'cancelled').length}</span>
                    </button>
                  </div>

                  <div className="enquiries-list">
                    {getFilteredAndSortedEnquiries().length === 0 ? (
                      <div className="empty-state-large">
                        <div className="empty-icon">
                          <FaEnvelope />
                        </div>
                        <h3>No enquiries found</h3>
                        <p>
                          {enquirySearchTerm 
                            ? 'Try adjusting your search terms' 
                            : enquiryFilter !== 'all'
                            ? `No ${enquiryFilter} enquiries at the moment`
                            : 'Guest booking requests will appear here once they book your properties'}
                        </p>
                        {(enquirySearchTerm || enquiryFilter !== 'all') && (
                          <button 
                            className="btn-clear-filters"
                            onClick={() => {
                              setEnquirySearchTerm('');
                              setEnquiryFilter('all');
                            }}
                          >
                            Clear Filters
                          </button>
                        )}
                      </div>
                    ) : (
                      getFilteredAndSortedEnquiries().map(enquiry => (
                        <motion.div
                          key={enquiry.id}
                          id={`enquiry-card-${enquiry.id}`}
                          className={`enquiry-card enquiry-status-${enquiry.status}${
                            selectedEnquiryId && String(enquiry.id) === String(selectedEnquiryId)
                              ? ' enquiry-card--focused'
                              : ''
                          }`}
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.2 }}
                        >
                          {/* Status Badge */}
                          <div className={`enquiry-status-badge status-${enquiry.status}`}>
                            {enquiry.status === 'pending' && <FaEnvelope />}
                            {enquiry.status === 'approved' && <FaCheckCircle />}
                            {enquiry.status === 'confirmed' && <FaCheckCircle />}
                            {enquiry.status === 'declined' && <FaTimesCircle />}
                            {enquiry.status === 'cancelled' && <FaTimesCircle />}
                            <span>{enquiry.status === 'confirmed' ? 'approved' : enquiry.status}</span>
                          </div>

                          {/* Cancellation Info (if cancelled) */}
                          {enquiry.status === 'cancelled' && enquiry.cancelReason && (
                            <div style={{
                              background: '#fff3cd',
                              border: '1px solid #ffc107',
                              borderRadius: '8px',
                              padding: '12px',
                              marginTop: '10px',
                              fontSize: '14px'
                            }}>
                              <div style={{ fontWeight: 'bold', marginBottom: '5px', color: '#856404' }}>
                                ❌ Cancellation Details
                              </div>
                              <div style={{ color: '#856404' }}>
                                <strong>Reason:</strong> {enquiry.cancelReason}
                              </div>
                              {enquiry.canceledBy && (
                                <div style={{ color: '#856404', marginTop: '3px' }}>
                                  <strong>Cancelled by:</strong> {enquiry.canceledBy}
                                </div>
                              )}
                              {enquiry.cancelledAt && (
                                <div style={{ color: '#856404', marginTop: '3px' }}>
                                  <strong>Cancelled on:</strong> {formatDate(enquiry.cancelledAt)}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Guest Info Header */}
                          <div className="enquiry-header">
                            <div className="guest-avatar">
                              <FaUser />
                            </div>
                            <div className="guest-info">
                              <h3>{getEnquiryBookedByLabel(enquiry)}</h3>
                              {getEnquiryCompanyName(enquiry) && (
                                <p className="guest-email">Company: {getEnquiryCompanyName(enquiry)}</p>
                              )}
                              {enquiry.staffDetails?.employeeId && (
                                <p className="guest-email">Employee ID: {enquiry.staffDetails.employeeId}</p>
                              )}
                              <p className="guest-email">{enquiry.guestEmail}</p>
                              <p className="enquiry-date">
                                <FaCalendarAlt />
                                Received {formatDate(enquiry.createdAt)}
                              </p>
                            </div>
                          </div>

                          {/* Property & Booking Details */}
                          <div className="enquiry-body">
                            <div className="property-section">
                              <h4 className="property-title">
                                <FaHome />
                                {enquiry.propertyName}
                              </h4>
                            </div>

                            <div className="booking-details-grid">
                              <div className="detail-card">
                                <div className="detail-icon">
                                  <FaCalendarAlt />
                                </div>
                                <div className="detail-content">
                                  <span className="detail-label">Check-in</span>
                                  <span className="detail-value">{formatDate(enquiry.checkIn)}</span>
                                </div>
                              </div>

                              <div className="detail-card">
                                <div className="detail-icon">
                                  <FaCalendarAlt />
                                </div>
                                <div className="detail-content">
                                  <span className="detail-label">Check-out</span>
                                  <span className="detail-value">{formatDate(enquiry.checkOut)}</span>
                                </div>
                              </div>

                              <div className="detail-card">
                                <div className="detail-icon">
                                  <FaUsers />
                                </div>
                                <div className="detail-content">
                                  <span className="detail-label">Guests</span>
                                  <span className="detail-value">{enquiry.guests}</span>
                                </div>
                              </div>

                              <div className="detail-card">
                                <div className="detail-icon">
                                  <FaRupeeSign />
                                </div>
                                <div className="detail-content">
                                  <span className="detail-label">Total Amount</span>
                                  <span className="detail-value">₹{enquiry.totalAmount.toLocaleString()}</span>
                                </div>
                              </div>

                              {enquiry.arrivalTime && (
                                <div className="detail-card arrival-time-highlight">
                                  <div className="detail-icon">
                                    <FaClock />
                                  </div>
                                  <div className="detail-content">
                                    <span className="detail-label">Expected Arrival</span>
                                    <span className="detail-value arrival-time-value">
                                      {enquiry.arrivalTime.includes('-') 
                                        ? enquiry.arrivalTime.split('-').map(t => {
                                            const [h, m] = t.trim().split(':');
                                            const hour = parseInt(h, 10);
                                            const ampm = hour >= 12 ? 'PM' : 'AM';
                                            const h12 = hour % 12 || 12;
                                            return `${h12}:${m || '00'} ${ampm}`;
                                          }).join(' - ')
                                        : enquiry.arrivalTime}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>

                            <div className="nights-info">
                              <span>{calculateNights(enquiry.checkIn, enquiry.checkOut)} nights</span>
                            </div>
                            {enquiry.staffDetails?.mobileNumber && (
                              <div className="nights-info">
                                <span>Staff Mobile: {enquiry.staffDetails.mobileNumber}</span>
                              </div>
                            )}

                            {enquiry.bookingType !== 'meeting' && enquiry.message && (
                              <div className="guest-message">
                                <h5>
                                  <FaComments />
                                  Message from guest
                                </h5>
                                <p>{enquiry.message}</p>
                              </div>
                            )}
                          </div>

                          {/* Action Buttons */}
                          {canRespondToEnquiry(enquiry) && (
                            <div className="enquiry-actions">
                              <button 
                                className="btn-action-approve"
                                disabled={enquiryActionLoading[enquiry.id]}
                                onClick={() => {
                                  if (enquiryActionLoading[enquiry.id]) return;
                                  setBookingActionConfirm({
                                    variant: 'approve',
                                    title: `Approve booking for ${getEnquiryBookedByLabel(enquiry)}?`,
                                    message: 'The guest will be notified that this booking is approved.',
                                    details: [
                                      { label: 'Property', value: enquiry.propertyName },
                                      {
                                        label: 'Dates',
                                        value: `${formatDate(enquiry.checkIn)} - ${formatDate(enquiry.checkOut)}`,
                                      },
                                      { label: 'Amount', value: `₹${enquiry.totalAmount.toLocaleString()}` },
                                    ],
                                    confirmText: 'Approve Booking',
                                    onConfirm: async () => {
                                      setBookingConfirmLoading(true);
                                      try {
                                        await handleEnquiryAction(enquiry.id, 'approved');
                                        setBookingActionConfirm(null);
                                      } finally {
                                        setBookingConfirmLoading(false);
                                      }
                                    },
                                  });
                                }}
                              >
                                {enquiryActionLoading[enquiry.id] === 'approved' ? <FaSpinner className="spin" /> : <FaCheckCircle />}
                                <span>{enquiryActionLoading[enquiry.id] === 'approved' ? 'Approving...' : 'Approve Booking'}</span>
                              </button>
                              <button 
                                className="btn-action-decline"
                                disabled={enquiryActionLoading[enquiry.id]}
                                onClick={() => {
                                  if (enquiryActionLoading[enquiry.id]) return;
                                  setBookingActionConfirm({
                                    variant: 'decline',
                                    title: `Decline booking request from ${getEnquiryBookedByLabel(enquiry)}?`,
                                    message: 'The guest will be notified that this booking request was declined.',
                                    details: [
                                      { label: 'Property', value: enquiry.propertyName },
                                      {
                                        label: 'Dates',
                                        value: `${formatDate(enquiry.checkIn)} - ${formatDate(enquiry.checkOut)}`,
                                      },
                                    ],
                                    confirmText: 'Decline Booking',
                                    onConfirm: async () => {
                                      setBookingConfirmLoading(true);
                                      try {
                                        await handleEnquiryAction(enquiry.id, 'declined');
                                        setBookingActionConfirm(null);
                                      } finally {
                                        setBookingConfirmLoading(false);
                                      }
                                    },
                                  });
                                }}
                              >
                                {enquiryActionLoading[enquiry.id] === 'declined' ? <FaSpinner className="spin" /> : <FaTimesCircle />}
                                <span>{enquiryActionLoading[enquiry.id] === 'declined' ? 'Declining...' : 'Decline'}</span>
                              </button>
                            </div>
                          )}

                          {/* Confirmation Message for Approved/Declined */}
                          {(enquiry.status === 'approved' || enquiry.status === 'confirmed') && (
                            <div className="enquiry-confirmation approved">
                              <FaCheckCircle />
                              <span>Booking approved! Guest has been notified.</span>
                            </div>
                          )}

                          {enquiry.status === 'declined' && (
                            <div className="enquiry-confirmation declined">
                              <FaTimesCircle />
                              <span>Booking declined. Guest has been notified.</span>
                            </div>
                          )}
                        </motion.div>
                      ))
                    )}
                  </div>
                </motion.div>
              )}

              {activeTab === 'notifications' && (
                <motion.div
                  key="notifications"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="notifications-tab-section"
                >
                  <div className="section-header">
                    <div>
                      <h1>Notifications</h1>
                      <p>Stay updated with your property activities</p>
                    </div>
                    <div className="notification-actions">
                      {notifications.length > 0 && (
                        <button 
                          className="btn-clear-all-notifications"
                          onClick={async () => {
                            if (!window.confirm('Are you sure you want to clear all notifications? This cannot be undone.')) return;
                            const token = localStorage.getItem('token');
                            const user = currentUser;
                            if (!token || !user) {
                              toast.info('Please sign in to clear notifications');
                              return;
                            }
                            try {
                              const url = `${getApiUrl('/api/notifications/clear-all')}?userType=host`;
                              const response = await fetch(url, {
                                method: 'DELETE',
                                headers: { 
                                  'Authorization': `Bearer ${token}`,
                                  'Content-Type': 'application/json'
                                }
                              });
                              const result = await response.json().catch(() => ({}));
                              if (response.ok && result.success) {
                                setNotifications([]);
                                setUnreadNotifCount(0);
                                loadNotificationsAndMessages(user);
                              } else {
                                toast.error(result.message || 'Failed to clear notifications');
                              }
                            } catch (error) {
                              console.error('Error clearing notifications:', error);
                              toast.error('Failed to clear notifications. Please try again.');
                            }
                          }}
                          title="Clear all notifications"
                        >
                          <FaTrash />
                          Clear all
                        </button>
                      )}
                      {notifications.length > 0 && unreadNotifCount > 0 && (
                        <button 
                          className="btn-mark-all-read"
                          onClick={() => {
                            const userId = currentUser?._id || currentUser?.id;
                            const notifKey = `notifications_host_${userId}`;
                            const updatedNotifs = notifications.map(n => ({ ...n, read: true }));
                            localStorage.setItem(notifKey, JSON.stringify(updatedNotifs));
                            setNotifications(updatedNotifs);
                            setUnreadNotifCount(0);
                          }}
                        >
                          Mark all as read
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="host-notification-filter-tabs">
                    <button
                      type="button"
                      className={`host-notification-filter-btn ${notificationCategory === 'meeting' ? 'active' : ''}`}
                      onClick={() => setNotificationCategory('meeting')}
                    >
                      Meeting Rooms
                      <span className="host-notification-filter-count">{meetingNotifications.length}</span>
                    </button>
                    <button
                      type="button"
                      className={`host-notification-filter-btn ${notificationCategory === 'hotel' ? 'active' : ''}`}
                      onClick={() => setNotificationCategory('hotel')}
                    >
                      Hotel Rooms
                      <span className="host-notification-filter-count">{hotelNotifications.length}</span>
                    </button>
                  </div>
                  
                  {filteredNotifications.length === 0 ? (
                    <div className="empty-state-large">
                      <div className="empty-icon">
                        <FaBell />
                      </div>
                      <h3>No {notificationCategory === 'meeting' ? 'meeting room' : 'hotel room'} notifications yet</h3>
                      <p>
                        {notificationCategory === 'meeting'
                          ? "You'll see meeting room booking notifications here."
                          : "You'll see hotel booking notifications here."}
                      </p>
                    </div>
                  ) : (
                    <div className="notifications-list-host">
                      {filteredNotifications.map(notif => {
                        // Safely convert any ObjectId fields to strings (avoid null.toString())
                        const rawId = notif.id ?? notif._id;
                        const notifId = rawId != null && typeof rawId === 'object' ? rawId.toString() : rawId;
                        const rawBookingId = notif.bookingId;
                        const bookingId = rawBookingId == null
                          ? ''
                          : (typeof rawBookingId === 'string'
                            ? rawBookingId
                            : (rawBookingId._id != null
                              ? String(rawBookingId._id)
                              : String(rawBookingId)));
                        const companyName = String(
                          notif?.metadata?.companyName ||
                          notif?.metadata?.companySnapshot?.name ||
                          ''
                        ).trim();
                        const staffName = String(notif?.metadata?.staffDetails?.name || '').trim();
                        const staffRole = String(notif?.metadata?.staffDetails?.role || '').trim();
                        const staffEmployeeId = String(notif?.metadata?.staffDetails?.employeeId || '').trim();
                        const bookedByLabel = staffName
                          ? (staffRole ? `${staffName} (${staffRole})` : staffName)
                          : '';
                        const showMeetingCard =
                          isMeetingNotification(notif) && hasRichMeetingMetadata(notif);

                        const handleHostNotificationClick = () => {
                          const userId = currentUser?._id || currentUser?.id;
                          const notifKey = `notifications_host_${userId}`;
                          const updatedNotifs = notifications.map(n =>
                            (n.id || n._id) === (notif.id || notif._id) ? { ...n, read: true } : n
                          );
                          localStorage.setItem(notifKey, JSON.stringify(updatedNotifs));
                          setNotifications(updatedNotifs);
                          setUnreadNotifCount(updatedNotifs.filter(n => !n.read).length);

                          const actionUrl = notif.actionUrl || '';
                          const isMeetingNotif = isMeetingNotification(notif);
                          const isHotelNotif = isHotelBookingNotification(notif);
                          const targetBookingId = bookingId || (
                            notif.bookingId && (
                              typeof notif.bookingId === 'string'
                                ? notif.bookingId
                                : (notif.bookingId._id ? String(notif.bookingId._id) : String(notif.bookingId))
                            )
                          );

                          if (isMeetingNotif || isHotelNotif) {
                            openEnquiryFromBooking(
                              targetBookingId,
                              isMeetingNotif ? 'meeting' : 'hotel'
                            );
                            return;
                          }

                          if (actionUrl && typeof actionUrl === 'string' && actionUrl.startsWith('/')) {
                            navigate(actionUrl);
                          } else if (notif.type === 'review_received' && notif.propertyId) {
                            const propId = typeof notif.propertyId === 'object' ? notif.propertyId?._id || notif.propertyId : notif.propertyId;
                            if (propId) navigate(`/host/property/${propId}`);
                          } else if (notif.type === 'guest_message') {
                            navigate('/host/messages', { state: bookingId ? { openBookingId: bookingId } : {} });
                          } else if (notif.type === 'booking_request' || notif.actionRequired) {
                            setActiveTab('enquiries');
                          }
                        };

                        if (showMeetingCard) {
                          return (
                            <MeetingBookingNotificationCard
                              key={notifId}
                              title={notif.title}
                              headline={notif.metadata?.headline || notif.title}
                              statusLabel={notif.metadata?.statusLabel}
                              teamName={notif.metadata?.teamName}
                              companyName={companyName}
                              metadata={notif.metadata}
                              createdAt={formatDate(notif.createdAt)}
                              read={notif.read}
                              onClick={handleHostNotificationClick}
                              className="host-meeting-notif-card"
                            />
                          );
                        }

                        return (
                        <div 
                          key={notifId} 
                          className={`notification-card-host ${!notif.read ? 'unread' : ''}`}
                          onClick={handleHostNotificationClick}
                        >
                          <div className="notif-icon-wrapper">
                            {notif.type === 'guest_message' && <FaQuestionCircle />}
                            {notif.type === 'booking_request' && <FaEnvelope />}
                            {(notif.type === 'booking_approved' || notif.type === 'booking_confirmed') && <FaCheckCircle />}
                            {notif.type === 'booking_declined' && <FaTimesCircle />}
                            {notif.type === 'review_received' && <FaStar />}
                            {notif.type === 'meeting_room_booked' && <FaCalendarAlt />}
                            {!['guest_message', 'booking_request', 'booking_approved', 'booking_confirmed', 'booking_declined', 'review_received', 'meeting_room_booked'].includes(notif.type) && <FaBell />}
                          </div>
                          <div className="notif-content-wrapper">
                            <div className="notif-header">
                              <h3>{notif.title}</h3>
                              <span className="notif-time">{formatDate(notif.createdAt)}</span>
                            </div>
                            <p className="notif-message">{notif.message}</p>
                            {(notif.propertyName || companyName || bookedByLabel || staffEmployeeId) && (
                              <div className="notif-meta">
                                {notif.propertyName && (
                                  <span className="notif-property">
                                    <FaHome />
                                    {notif.propertyName}
                                  </span>
                                )}
                                {companyName && (
                                  <span className="notif-booking-id">Company: {companyName}</span>
                                )}
                                {bookedByLabel && (
                                  <span className="notif-booking-id">Booked By: {bookedByLabel}</span>
                                )}
                                {staffEmployeeId && (
                                  <span className="notif-booking-id">Employee ID: {staffEmployeeId}</span>
                                )}
                                {bookingId && (
                                  <span className="notif-booking-id">
                                    Booking #{bookingId}
                                  </span>
                                )}
                              </div>
                            )}
                            {notif.actionRequired && (
                              <div className="notif-action-badge">
                                Action Required
                              </div>
                            )}
                          </div>
                          {!notif.read && <div className="unread-indicator-host"></div>}
                        </div>
                        );
                      })}
                    </div>
                  )}
                </motion.div>
              )}

              {activeTab === 'messages' && (
                <motion.div
                  key="messages"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="messages-tab-section"
                >
                  <HostMessages 
                    hostId={currentUser?._id || currentUser?.id || 'demo_host'}
                    hostName={currentUser?.name || 'Host'}
                  />
                </motion.div>
              )}

              {activeTab === 'properties' && (
                <motion.div
                  key="properties"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="properties-section"
                >
                  <div className="section-header">
                    <div>
                      <h1>{listingView === 'meeting' ? 'My Meeting Rooms' : 'My Properties'}</h1>
                      <p>
                        {activeListings.length} {activeListings.length === 1 ? activeListingLabel : `${activeListingLabel}s`} listed
                      </p>
                      <div className="listing-filter-tabs">
                        <button
                          type="button"
                          className={`listing-filter-btn ${listingView === 'stay' ? 'active' : ''}`}
                          onClick={() => setListingView('stay')}
                        >
                          My Properties
                        </button>
                        <button
                          type="button"
                          className={`listing-filter-btn ${listingView === 'meeting' ? 'active' : ''}`}
                          onClick={() => setListingView('meeting')}
                        >
                          My Meeting Rooms
                        </button>
                      </div>
                    </div>
                    {canManageListings && (
                      <button
                        className="btn-add-property"
                        onClick={openPropertyTypeModal}
                      >
                        <FaPlus />
                        <span>Add Property</span>
                      </button>
                    )}
                  </div>

                  {activeListings.length === 0 ? (
                    <div className="empty-state">
                      <div className="empty-icon">
                        <FaHome />
                      </div>
                      <h3>{listingView === 'meeting' ? 'No meeting rooms yet' : 'No properties yet'}</h3>
                      <p>
                        {listingView === 'meeting'
                          ? 'Start by adding your first meeting room'
                          : 'Start by adding your first property to begin hosting'}
                      </p>
                      {canManageListings && (
                        <button
                          className="btn-primary"
                          onClick={openPropertyTypeModal}
                        >
                          <FaPlus />
                          Add Your First Property
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="properties-grid">
                      {listingView === 'stay' ? properties.map(property => (
                        <div key={property._id || property.id} className="property-card">
                          <div className="property-image">
                            <img 
                              src={getPropertyImage(property, 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=400')} 
                              alt={property.name} 
                            />
                            <div className={`property-status-badge ${property.status}`}>
                              {property.status === 'active' ? 'Active' : 'Inactive'}
                            </div>
                          </div>
                          
                          <div className="property-info">
                            <h3>{property.name}</h3>
                            <p className="property-location">
                              <FaMapMarkerAlt />
                              {property.city}
                            </p>
                            
                            <div className="property-details">
                              <span className="property-type">{property.type}</span>
                              <span className="property-detail-item">
                                <FaUsers />
                                {property.maxGuests || property.guests}
                              </span>
                              <span className="property-detail-item">
                                <FaHome />
                                {property.bedrooms} BR
                              </span>
                            </div>
                            
                            <div className="property-stats">
                              <div className="property-rating">
                                <FaStar />
                                <span>{property.rating || 'New'}</span>
                                {property.reviews > 0 && (
                                  <span className="review-count">({property.reviews})</span>
                                )}
                              </div>
                              <div className="property-price">
                                ₹{property.pricePerNight?.toLocaleString()}
                                <span className="price-period">/night</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="property-actions">
                            <button 
                              className="btn-action btn-view"
                              title="View Details"
                              onClick={() => {
                                const propertyId = property._id || property.id;
                                // Navigate to host property details page with property data
                                navigate(`/host/property/${propertyId}`, {
                                  state: { property }
                                });
                              }}
                            >
                              <FaEye />
                            </button>
                            {canManageListings && (
                              <>
                                <button
                                  className="btn-action btn-edit"
                                  title="Edit Property"
                                  onClick={() => handleEditProperty(property)}
                                >
                                  <FaEdit />
                                </button>
                                <button
                                  className="btn-action btn-toggle"
                                  title={property.status === 'active' ? 'Deactivate' : 'Activate'}
                                  onClick={() => handleToggleStatus(property._id || property.id)}
                                >
                                  {property.status === 'active' ? <FaToggleOn /> : <FaToggleOff />}
                                </button>
                                <button
                                  className="btn-action btn-delete"
                                  title="Delete Property"
                                  onClick={() => handleDeleteProperty(property._id || property.id)}
                                >
                                  <FaTrash />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      )) : meetingRooms.map((room, index) => {
                        const roomId = room._id || room.id || `local-room-${index}`;
                        const image = room.images?.[0] || 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=400';
                        const status = room.isActive === false ? 'inactive' : 'active';
                        const capacity = room.capacity || room.maxCapacity || 0;
                        return (
                          <div key={roomId} className="property-card">
                            <div className="property-image">
                              <img src={image} alt={room.name || room.roomName || 'Meeting Room'} />
                              <div className={`property-status-badge ${status}`}>
                                {status === 'active' ? 'Active' : 'Inactive'}
                              </div>
                            </div>

                            <div className="property-info">
                              <h3>{room.name || room.roomName || 'Meeting Room'}</h3>
                              <p className="property-location">
                                <FaMapMarkerAlt />
                                {room.city || room.location || room.address || 'Location not set'}
                              </p>

                              <div className="property-details">
                                <span className="property-type">{room.roomType || 'Meeting Room'}</span>
                                <span className="property-detail-item">
                                  <FaUsers />
                                  {capacity}
                                </span>
                              </div>

                              <div className="property-stats">
                                <div className="property-rating">
                                  <FaStar />
                                  <span>{room.cleaningResponsibility === 'owner_clean' ? 'Owner Cleaning' : 'Member Cleaning'}</span>
                                </div>
                                <div className="property-price">
                                  ₹{Number(room.pricePerHour || 0).toLocaleString()}
                                  <span className="price-period">/hour</span>
                                </div>
                              </div>
                            </div>

                            <div className="property-actions">
                              <button
                                className="btn-action btn-view"
                                title="View Meeting Room"
                                onClick={() => handleViewMeetingRoom(room)}
                              >
                                <FaEye />
                              </button>
                              {canManageListings && (
                                <>
                                  <button
                                    className="btn-action btn-edit"
                                    title="Edit Meeting Room"
                                    onClick={() => handleEditMeetingRoom(room)}
                                  >
                                    <FaEdit />
                                  </button>
                                  <button
                                    className="btn-action btn-toggle"
                                    title={status === 'active' ? 'Deactivate Meeting Room' : 'Activate Meeting Room'}
                                    onClick={() => handleToggleMeetingRoom(roomId)}
                                  >
                                    {status === 'active' ? <FaToggleOn /> : <FaToggleOff />}
                                  </button>
                                  <button
                                    className="btn-action btn-delete"
                                    title="Delete Meeting Room"
                                    onClick={() => handleDeleteMeetingRoom(room)}
                                  >
                                    <FaTrash />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </motion.div>
              )}

              {activeTab === 'calendar' && (
                <motion.div
                  key="calendar"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="calendar-section"
                >
                  <div className="section-header">
                    <div>
                      <h1>Booking Calendar</h1>
                      <p>View and manage your property bookings</p>
                    </div>
                  </div>

                  <div className="calendar-container">
                    <div className="calendar-header">
                      <button 
                        className="calendar-nav-btn"
                        onClick={() => {
                          const newDate = new Date(currentMonth);
                          newDate.setMonth(newDate.getMonth() - 1);
                          setCurrentMonth(newDate);
                        }}
                      >
                        ‹
                      </button>
                      <h2 className="calendar-month">
                        {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </h2>
                      <button 
                        className="calendar-nav-btn"
                        onClick={() => {
                          const newDate = new Date(currentMonth);
                          newDate.setMonth(newDate.getMonth() + 1);
                          setCurrentMonth(newDate);
                        }}
                      >
                        ›
                      </button>
                    </div>

                    <div className="calendar-grid">
                      <div className="calendar-weekdays">
                        <div className="weekday">Sun</div>
                        <div className="weekday">Mon</div>
                        <div className="weekday">Tue</div>
                        <div className="weekday">Wed</div>
                        <div className="weekday">Thu</div>
                        <div className="weekday">Fri</div>
                        <div className="weekday">Sat</div>
                      </div>

                      <div className="calendar-days">
                        {(() => {
                          const year = currentMonth.getFullYear();
                          const month = currentMonth.getMonth();
                          const firstDay = new Date(year, month, 1).getDay();
                          const daysInMonth = new Date(year, month + 1, 0).getDate();
                          const today = new Date();
                          const days = [];

                          // Empty cells for days before month starts
                          for (let i = 0; i < firstDay; i++) {
                            days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
                          }

                          // Days of the month
                          for (let day = 1; day <= daysInMonth; day++) {
                            const date = new Date(year, month, day);
                            const isToday = date.toDateString() === today.toDateString();
                            const isPast = date < today && !isToday;
                            const isSelected = selectedCalendarDate && date.toDateString() === selectedCalendarDate.toDateString();

                            const hasBooking = enquiries.some(enquiry => {
                              const checkIn = new Date(enquiry.checkIn);
                              const checkOut = new Date(enquiry.checkOut);
                              return date >= checkIn && date <= checkOut && (enquiry.status === 'approved' || enquiry.status === 'confirmed');
                            });

                            days.push(
                              <button
                                type="button"
                                key={day}
                                className={`calendar-day ${isToday ? 'today' : ''} ${isPast ? 'past' : ''} ${hasBooking ? 'has-booking' : ''} ${isSelected ? 'selected' : ''}`}
                                onClick={() => setSelectedCalendarDate(date)}
                                aria-label={`${day} ${isToday ? '(Today)' : ''} ${hasBooking ? '- has booking' : ''}`}
                              >
                                <span className="day-number">{day}</span>
                                {hasBooking && <div className="booking-indicator"></div>}
                              </button>
                            );
                          }

                          return days;
                        })()}
                      </div>
                    </div>

                    {/* Calendar Legend */}
                    <div className="calendar-legend">
                      <div className="legend-item">
                        <div className="legend-color today-color"></div>
                        <span>Today</span>
                      </div>
                      <div className="legend-item">
                        <div className="legend-color booking-color"></div>
                        <span>Booked</span>
                      </div>
                      <div className="legend-item">
                        <div className="legend-color available-color"></div>
                        <span>Available</span>
                      </div>
                    </div>

                    {/* Bookings on selected date */}
                    {selectedCalendarDate && (() => {
                      const dayStart = new Date(selectedCalendarDate);
                      dayStart.setHours(0, 0, 0, 0);
                      const dayEnd = new Date(selectedCalendarDate);
                      dayEnd.setHours(23, 59, 59, 999);
                      const bookingsOnDay = enquiries.filter(e => {
                        if (e.status !== 'approved' && e.status !== 'confirmed') return false;
                        const checkIn = new Date(e.checkIn);
                        const checkOut = new Date(e.checkOut);
                        return dayStart <= checkOut && dayEnd >= checkIn;
                      });
                      return (
                        <div className="calendar-date-bookings-panel">
                          <h3>Bookings on {formatDate(selectedCalendarDate)}</h3>
                          {bookingsOnDay.length > 0 ? (
                            <>
                              <div className="booking-list">
                                {bookingsOnDay.map(booking => (
                                  <div key={booking.id} className="booking-item">
                                    <div className="booking-date">
                                      <FaCalendarAlt />
                                      <span>{formatDate(booking.checkIn)} – {formatDate(booking.checkOut)}</span>
                                    </div>
                                    <div className="booking-details">
                                      <span className="booking-guest">{booking.guestName}</span>
                                      <span className="booking-property">{booking.propertyName}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                              <button type="button" className="btn-view-enquiries" onClick={() => setActiveTab('enquiries')}>
                                View in Enquiries
                              </button>
                            </>
                          ) : (
                            <p className="no-bookings-message">No bookings on this day.</p>
                          )}
                        </div>
                      );
                    })()}

                    {/* Upcoming Bookings */}
                    <div className="upcoming-bookings">
                      <h3>Upcoming Bookings</h3>
                      {enquiries.filter(e => (e.status === 'approved' || e.status === 'confirmed') && new Date(e.checkIn) >= new Date()).length > 0 ? (
                        <div className="booking-list">
                          {enquiries
                            .filter(e => (e.status === 'approved' || e.status === 'confirmed') && new Date(e.checkIn) >= new Date())
                            .sort((a, b) => new Date(a.checkIn) - new Date(b.checkIn))
                            .slice(0, 5)
                            .map(booking => (
                              <div key={booking.id} className="booking-item">
                                <div className="booking-date">
                                  <FaCalendarAlt />
                                  <span>{formatDate(booking.checkIn)} - {formatDate(booking.checkOut)}</span>
                                </div>
                                <div className="booking-details">
                                  <span className="booking-guest">{booking.guestName}</span>
                                  <span className="booking-property">{booking.propertyName}</span>
                                </div>
                              </div>
                            ))}
                        </div>
                      ) : (
                        <div className="upcoming-bookings-empty">
                          <p>No upcoming bookings.</p>
                          <button type="button" className="btn-view-enquiries" onClick={() => setActiveTab('enquiries')}>
                            View Enquiries
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}

              {activeTab === 'profile' && (
                <motion.div
                  key="profile"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="profile-section"
                >
                  <div className="section-header">
                    <div>
                      <h1>Host Profile</h1>
                      <p>Manage your personal information</p>
                    </div>
                    {!isEditingProfile ? (
                      <button className="btn-edit-profile" onClick={handleProfileEdit}>
                        <FaEdit />
                        <span>Edit Profile</span>
                      </button>
                    ) : (
                      <div className="profile-edit-actions">
                        <button className="btn-cancel-profile" onClick={handleProfileCancel}>
                          <FaTimes />
                          <span>Cancel</span>
                        </button>
                        <button className="btn-save-profile" onClick={handleProfileSave}>
                          <FaCheckCircle />
                          <span>Save Changes</span>
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="profile-content">
                    {/* Profile Header Card */}
                    <div className="profile-header-card">
                      <div className="profile-avatar-section">
                        <div className="profile-avatar-large">
                          {getProfileImageUrl(currentUser?.profilePicture) ? (
                            <img 
                              src={getProfileImageUrl(currentUser.profilePicture)} 
                              alt={profileData.name || 'Host'} 
                            />
                          ) : (
                            <div className="avatar-placeholder-large">
                              <FaUser size={64} />
                            </div>
                          )}
                          {isEditingProfile && (
                            <button className="btn-change-avatar" title="Change Photo">
                              <FaEdit />
                            </button>
                          )}
                        </div>
                        <div className="profile-header-info">
                          {!isEditingProfile ? (
                            <>
                              <h2>{profileData.name || 'Host Name'}</h2>
                              <p className="profile-email">{profileData.email}</p>
                              <div className="profile-badges">
                                <span className="badge-verified">
                                  <FaCheckCircle />
                                  Verified Host
                                </span>
                                <span className="badge-member">Member since 2024</span>
                              </div>
                            </>
                          ) : (
                            <>
                              <input
                                type="text"
                                className="profile-input-large"
                                value={profileData.name}
                                onChange={(e) => handleProfileChange('name', e.target.value)}
                                placeholder="Your Name"
                              />
                              <input
                                type="email"
                                className="profile-input-email"
                                value={profileData.email}
                                onChange={(e) => handleProfileChange('email', e.target.value)}
                                placeholder="your.email@example.com"
                              />
                            </>
                          )}
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="profile-stats-row">
                        <div className="stat-box-profile">
                          <div className="stat-icon-profile blue">
                            <FaHome />
                          </div>
                          <div className="stat-content-profile">
                            <div className="stat-value-profile">{properties.length}</div>
                            <div className="stat-label-profile">Properties</div>
                          </div>
                        </div>
                        <div className="stat-box-profile">
                          <div className="stat-icon-profile green">
                            <FaEnvelope />
                          </div>
                          <div className="stat-content-profile">
                            <div className="stat-value-profile">{enquiries.length}</div>
                            <div className="stat-label-profile">Enquiries</div>
                          </div>
                        </div>
                        <div className="stat-box-profile">
                          <div className="stat-icon-profile orange">
                            <FaStar />
                          </div>
                          <div className="stat-content-profile">
                            <div className="stat-value-profile">4.8</div>
                            <div className="stat-label-profile">Rating</div>
                          </div>
                        </div>
                        <div className="stat-box-profile">
                          <div className="stat-icon-profile purple">
                            <FaRupeeSign />
                          </div>
                          <div className="stat-content-profile">
                            <div className="stat-value-profile">
                              ₹{enquiries.reduce((sum, e) => sum + e.totalAmount, 0).toLocaleString()}
                            </div>
                            <div className="stat-label-profile">Total Value</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Profile Details Grid */}
                    <div className="profile-details-grid">
                      {/* Personal Information */}
                      <div className="profile-detail-card">
                        <div className="card-header">
                          <h3>
                            <FaUser />
                            Personal Information
                          </h3>
                        </div>
                        <div className="card-content">
                          <div className="detail-row">
                            <label>Full Name</label>
                            {!isEditingProfile ? (
                              <span>{profileData.name || 'Not provided'}</span>
                            ) : (
                              <input
                                type="text"
                                className="profile-input"
                                value={profileData.name}
                                onChange={(e) => handleProfileChange('name', e.target.value)}
                                placeholder="Enter your full name"
                              />
                            )}
                          </div>
                          <div className="detail-row">
                            <label>Email</label>
                            {!isEditingProfile ? (
                              <span>{profileData.email}</span>
                            ) : (
                              <input
                                type="email"
                                className="profile-input"
                                value={profileData.email}
                                onChange={(e) => handleProfileChange('email', e.target.value)}
                                placeholder="your.email@example.com"
                              />
                            )}
                          </div>
                          <div className="detail-row">
                            <label>Phone</label>
                            {!isEditingProfile ? (
                              <span>{profileData.phone}</span>
                            ) : (
                              <input
                                type="tel"
                                className="profile-input"
                                value={profileData.phone}
                                onChange={(e) => handleProfileChange('phone', e.target.value)}
                                placeholder="+91 98765 43210"
                              />
                            )}
                          </div>
                          <div className="detail-row">
                            <label>Location</label>
                            {!isEditingProfile ? (
                              <span>{profileData.location || 'Not provided'}</span>
                            ) : (
                              <input
                                type="text"
                                className="profile-input"
                                value={profileData.location}
                                onChange={(e) => handleProfileChange('location', e.target.value)}
                                placeholder="City, State"
                              />
                            )}
                          </div>
                          <div className="detail-row">
                            <label>Date of Birth</label>
                            {!isEditingProfile ? (
                              <span>{profileData.dateOfBirth || 'Not provided'}</span>
                            ) : (
                              <input
                                type="text"
                                className="profile-input"
                                value={profileData.dateOfBirth}
                                onChange={(e) => handleProfileChange('dateOfBirth', e.target.value)}
                                placeholder="dd-mm-yyyy"
                              />
                            )}
                          </div>
                          <div className="detail-row">
                            <label>Address</label>
                            {!isEditingProfile ? (
                              <span>{profileData.address || 'Not provided'}</span>
                            ) : (
                              <input
                                type="text"
                                className="profile-input"
                                value={profileData.address}
                                onChange={(e) => handleProfileChange('address', e.target.value)}
                                placeholder="Enter your complete address"
                              />
                            )}
                          </div>
                          <div className="detail-row">
                            <label>City</label>
                            {!isEditingProfile ? (
                              <span>{profileData.city || 'Not provided'}</span>
                            ) : (
                              <input
                                type="text"
                                className="profile-input"
                                value={profileData.city}
                                onChange={(e) => handleProfileChange('city', e.target.value)}
                                placeholder="Enter your city"
                              />
                            )}
                          </div>
                          <div className="detail-row">
                            <label>State</label>
                            {!isEditingProfile ? (
                              <span>{profileData.state || 'Not provided'}</span>
                            ) : (
                              <input
                                type="text"
                                className="profile-input"
                                value={profileData.state}
                                onChange={(e) => handleProfileChange('state', e.target.value)}
                                placeholder="Enter your state"
                              />
                            )}
                          </div>
                          <div className="detail-row">
                            <label>PIN Code</label>
                            {!isEditingProfile ? (
                              <span>{profileData.pincode || 'Not provided'}</span>
                            ) : (
                              <input
                                type="text"
                                className="profile-input"
                                value={profileData.pincode}
                                onChange={(e) => handleProfileChange('pincode', e.target.value)}
                                placeholder="Enter PIN code"
                              />
                            )}
                          </div>
                          <div className="detail-row">
                            <label>Emergency Contact</label>
                            {!isEditingProfile ? (
                              <span>{profileData.emergencyContact || 'Not provided'}</span>
                            ) : (
                              <input
                                type="tel"
                                className="profile-input"
                                value={profileData.emergencyContact}
                                onChange={(e) => handleProfileChange('emergencyContact', e.target.value)}
                                placeholder="Emergency contact number"
                              />
                            )}
                          </div>
                          <div className="detail-row">
                            <label>Government ID Type</label>
                            {!isEditingProfile ? (
                              <span>{profileData.governmentId || 'Not provided'}</span>
                            ) : (
                              <select
                                className="profile-input"
                                value={profileData.governmentId}
                                onChange={(e) => handleProfileChange('governmentId', e.target.value)}
                              >
                                <option value="">Select ID Type</option>
                                <option value="Aadhaar">Aadhaar</option>
                                <option value="PAN">PAN</option>
                                <option value="Passport">Passport</option>
                                <option value="Driving License">Driving License</option>
                                <option value="Voter ID">Voter ID</option>
                              </select>
                            )}
                          </div>
                          <div className="detail-row">
                            <label>ID Number</label>
                            {!isEditingProfile ? (
                              <span>{profileData.governmentIdNumber || 'Not provided'}</span>
                            ) : (
                              <input
                                type="text"
                                className="profile-input"
                                value={profileData.governmentIdNumber}
                                onChange={(e) => handleProfileChange('governmentIdNumber', e.target.value)}
                                placeholder="Enter ID number"
                              />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Host Information */}
                      <div className="profile-detail-card">
                        <div className="card-header">
                          <h3>
                            <FaStar />
                            Host Information
                          </h3>
                        </div>
                        <div className="card-content">
                          <div className="detail-row">
                            <label>Host Since</label>
                            <span>January 2024</span>
                          </div>
                          <div className="detail-row">
                            <label>Response Rate</label>
                            <span className="highlight-green">98%</span>
                          </div>
                          <div className="detail-row">
                            <label>Response Time</label>
                            <span className="highlight-blue">Within 1 hour</span>
                          </div>
                          <div className="detail-row">
                            <label>Languages</label>
                            {!isEditingProfile ? (
                              <span>{profileData.languages}</span>
                            ) : (
                              <input
                                type="text"
                                className="profile-input"
                                value={profileData.languages}
                                onChange={(e) => handleProfileChange('languages', e.target.value)}
                                placeholder="English, Hindi, etc."
                              />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* About Me - Full Width */}
                      <div className="profile-detail-card profile-about-card">
                        <div className="card-header">
                          <h3>
                            <FaComments />
                            About Me
                          </h3>
                        </div>
                        <div className="card-content">
                          {!isEditingProfile ? (
                            <p className="profile-bio">{profileData.bio}</p>
                          ) : (
                            <textarea
                              className="profile-textarea"
                              value={profileData.bio}
                              onChange={(e) => handleProfileChange('bio', e.target.value)}
                              placeholder="Tell guests about yourself..."
                              rows="4"
                            />
                          )}
                          
                          <div className="interests-section">
                            <label>Interests</label>
                            <div className="profile-interests">
                              {profileData.interests.map((interest, index) => (
                                <span key={index} className="interest-tag">
                                  {interest}
                                  {isEditingProfile && (
                                    <button
                                      className="btn-remove-interest"
                                      onClick={() => handleRemoveInterest(index)}
                                      title="Remove"
                                    >
                                      <FaTimes />
                                    </button>
                                  )}
                                </span>
                              ))}
                              {isEditingProfile && (
                                <button className="btn-add-interest" onClick={handleAddInterest}>
                                  <FaPlus />
                                  Add Interest
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {activeTab === 'earnings' && (
                <motion.div
                  key="earnings"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="earnings-section"
                >
                  <h1>Earnings & Payouts</h1>
                  {earningsLoading ? (
                    <div className="earnings-loading"><FaSpinner className="fa-spin" /> Loading...</div>
                  ) : (
                    <>
                      <div className="earnings-summary-cards">
                        <div className="earnings-summary-card">
                          <span className="earnings-summary-label">Total paid by guests</span>
                          <span className="earnings-summary-value"><FaRupeeSign />{hostEarnings.payments.reduce((s, p) => s + (p.totalAmount || 0), 0).toLocaleString()}</span>
                        </div>
                        <div className="earnings-summary-card commission">
                          <span className="earnings-summary-label">App commission deducted</span>
                          <span className="earnings-summary-value"><FaRupeeSign />{(hostEarnings.summary.totalCommission ?? hostEarnings.payments.reduce((s, p) => s + (p.commissionAmount || 0), 0)).toLocaleString()}</span>
                        </div>
                        <div className="earnings-summary-card net">
                          <span className="earnings-summary-label">Your net balance</span>
                          <span className="earnings-summary-value"><FaRupeeSign />{(hostEarnings.summary.netBalance ?? hostEarnings.payments.reduce((s, p) => s + (p.hostAmount || 0), 0)).toLocaleString()}</span>
                        </div>
                      </div>
                      <div className="earnings-payments-block">
                        <h2>User payment details</h2>
                        {hostEarnings.payments.length === 0 ? (
                          <p className="earnings-empty">No paid bookings yet.</p>
                        ) : (
                          <div className="earnings-payments-table-wrap">
                            <table className="earnings-payments-table">
                              <thead>
                                <tr>
                                  <th>Guest</th>
                                  <th>Property</th>
                                  <th>Check-in</th>
                                  <th>Total (user paid)</th>
                                  <th>Commission</th>
                                  <th>Your payment</th>
                                  <th>Date</th>
                                </tr>
                              </thead>
                              <tbody>
                                {hostEarnings.payments.map((p) => (
                                  <tr key={p._id}>
                                    <td>{p.guestName || p.guestEmail || '—'}</td>
                                    <td>{p.propertyName || '—'}</td>
                                    <td>{p.checkIn ? new Date(p.checkIn).toLocaleDateString() : '—'}</td>
                                    <td><FaRupeeSign />{Number(p.totalAmount || 0).toLocaleString()}</td>
                                    <td><FaRupeeSign />{Number(p.commissionAmount || 0).toLocaleString()}</td>
                                    <td><FaRupeeSign />{Number(p.hostAmount || 0).toLocaleString()}</td>
                                    <td>{p.paidAt ? new Date(p.paidAt).toLocaleDateString() : '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                      <div className="earnings-payouts-block">
                        <h2>Payout history</h2>
                        <p className="earnings-payouts-desc">When the app transfers money to you</p>
                        {hostPayouts.length === 0 ? (
                          <p className="earnings-empty">No payouts yet.</p>
                        ) : (
                          <div className="earnings-payouts-table-wrap">
                            <table className="earnings-payouts-table">
                              <thead>
                                <tr>
                                  <th>Date</th>
                                  <th>Amount</th>
                                  <th>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {hostPayouts.map((p) => (
                                  <tr key={p._id}>
                                    <td>{p.paidAt ? new Date(p.paidAt).toLocaleDateString() : new Date(p.createdAt).toLocaleDateString()}</td>
                                    <td><FaRupeeSign />{Number(p.amount || 0).toLocaleString()}</td>
                                    <td><span className={`payout-status status-${p.status}`}>{p.status}</span></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </motion.div>
              )}

              {activeTab === 'settings' && (
                <motion.div
                  key="settings"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="settings-section"
                >
                  <h1>Account Settings</h1>
                  <HostSettingsPanel />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </main>

        <AddPropertyModal
          isOpen={showAddPropertyModal}
          onClose={() => setShowAddPropertyModal(false)}
          onPropertyAdded={handlePropertyAdded}
        />

        <EditPropertyModal
          isOpen={showEditPropertyModal}
          onClose={() => {
            setShowEditPropertyModal(false);
            setEditingProperty(null);
          }}
          property={editingProperty}
          onPropertyUpdated={handlePropertyUpdated}
        />

        <PropertyTypeModal
          isOpen={showPropertyTypeModal}
          onClose={() => {
            setShowPropertyTypeModal(false);
            setShowListingTypePicker(false);
          }}
          onSelectStayProperty={handleSelectStayProperty}
          onSelectMeetingRoom={handleSelectMeetingRoom}
        />

        <AddMeetingRoomForm
          isOpen={showMeetingRoomForm}
          onClose={() => setShowMeetingRoomForm(false)}
          onSubmit={handleMeetingRoomAdded}
        />
      </div>
    );
  }

  // If user is not logged in, show the landing page
  if (!currentUser) {
    return (
      <div className="host-page">
        <Navbar isScrolled={isScrolled} />
        
        <main className="host-landing">
          {/* Hero Section */}
          <section className="host-hero">
            <div className="hero-content">
              <div className="hero-text">
                <h1>Become an UBSAAPP Host</h1>
                <p className="hero-subtitle">
                  Transform your space into income. Join thousands of hosts earning extra money 
                  by sharing their properties with travelers worldwide.
                </p>
                
                <button 
                  className="btn-get-started"
                  onClick={handleSignInClick}
                >
                  Start Hosting Today
                </button>
              </div>
              <div className="hero-image">
                <img 
                  src="https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=600" 
                  alt="Host your property" 
                />
              </div>
            </div>
          </section>

          {/* How It Works Section */}
          <section className="how-it-works">
            <div className="container">
              <h2>How Hosting Works</h2>
              <p className="section-subtitle">
                Start earning in just 3 simple steps. We'll guide you through the entire process.
              </p>
              
              <div className="steps-grid">
                <div className="step-card">
                  <div className="step-number">1</div>
                  <div className="step-icon">🏠</div>
                  <h3>List Your Space</h3>
                  <p>
                    Create your listing with photos, description, and amenities. 
                    Set your own prices and availability calendar.
                  </p>
                  <ul className="step-details">
                    <li>Upload high-quality photos</li>
                    <li>Write compelling descriptions</li>
                    <li>Set competitive pricing</li>
                    <li>Define house rules</li>
                  </ul>
                </div>
                
                <div className="step-card">
                  <div className="step-number">2</div>
                  <div className="step-icon">👥</div>
                  <h3>Welcome Guests</h3>
                  <p>
                    Receive booking requests, communicate with guests, and provide 
                    exceptional hospitality experiences.
                  </p>
                  <ul className="step-details">
                    <li>Review booking requests</li>
                    <li>Communicate with guests</li>
                    <li>Provide local recommendations</li>
                    <li>Ensure smooth check-in/out</li>
                  </ul>
                </div>
                
                <div className="step-card">
                  <div className="step-number">3</div>
                  <div className="step-icon">💰</div>
                  <h3>Get Paid</h3>
                  <p>
                    Earn money automatically. Payments are processed securely 
                    and transferred to your account after each stay.
                  </p>
                  <ul className="step-details">
                    <li>Automatic payment processing</li>
                    <li>Secure bank transfers</li>
                    <li>Detailed earning reports</li>
                    <li>Tax documentation support</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="cta-section">
            <div className="container">
              <div className="cta-content">
                <h2>Ready to Start Hosting?</h2>
                <p>
                  Join thousands of successful hosts and start earning extra income today. 
                  It takes less than 10 minutes to get started.
                </p>
                <div className="cta-buttons">
                  <button 
                    className="btn-primary"
                    onClick={handleSignInClick}
                  >
                    Start Hosting Now
                  </button>
                  <button className="btn-secondary">
                    Learn More
                  </button>
                </div>
                <div className="cta-features">
                  <span>✓ Free to list</span>
                  <span>✓ No upfront costs</span>
                  <span>✓ 24/7 support</span>
                  <span>✓ Host protection</span>
                </div>
              </div>
            </div>
          </section>
        </main>

        <LoginModal 
          isOpen={showLoginModal} 
          onClose={() => setShowLoginModal(false)} 
        />
      </div>
    );
  }

  // Default fallback (should not reach here)
  return null;
}

export default HostPage;

